/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import type { CSSProperties, MouseEvent, ReactNode } from 'react';
import { Fragment, isValidElement, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './ComponentIcon';
import { Loading } from '../Loading';
import { FormBuilder, type FormField } from './FormBuilder';
import { FormModal } from './FormModal';
import { SmartFilter, type SmartFilterProps } from './SmartFilter';
import { DataTableToolsProvider } from './DataTableTools';
import { Pagination } from './Pagination';
import { useTranslation } from '../../hooks/useTranslation';
import { DEFAULT_LANGUAGE, type SupportedLanguage } from '../../utils/language.utils';
import { useDataTableUi } from '../../hooks/useDataTableUi';
import type { DataTableRowMode, DataTableTablePrefs } from '../../hooks/useDataTableUi';
import { useDateTime } from '../../hooks/useDateTime';
import { usePermission } from '../../hooks/usePermission';
import { useTableOperationManifest, type TableOperationManifestItem } from '../../hooks/useTableOperationManifest';
import { dataTableStorageTableId, type QubeProDataTableIdentity } from '../../config/app.config';
import { DEFAULT_PAGE_SIZE, normalizePageSize } from '../../config/pagination.config';
import {
    type DataTableCsvColumn,
} from './data-table-export.utils';
import {
    parseDataTableImportFile,
    type DataTableImportOptions,
    type DataTableImportPayload,
    type DataTableImportParseResult,
    type DataTableImportSubmitResult,
} from './data-table-import.utils';
import {
    DataTableCorporateOutputDialog,
    DataTableOutputExportDialog,
    type DataTableOutputCell,
    type DataTableOutputColumn,
    type DataTableOutputColumnFormat,
    type DataTableOutputColumnType,
    type DataTableOutputExportRequest,
    type DataTableOutputFormat,
    type DataTableOutputScope,
    useDataTableOutputExport,
} from './DataTableOutputExport';
import { isSensitiveDataTableOutputIdentifier } from './data-table-output.utils';
import {
    WorkspaceFocusModeToggle,
    useWorkspaceFocusMode,
} from './WorkspaceFocusMode';
import {
    QUBEPRO_FLOATING_LAYOUT_CHANGE_EVENT,
    readQubeProFloatingSafeArea,
} from './floating-layout.utils';
import './DataTable.css';
import { TableRowActionsSurface } from './TableRowActionsSurface';
import { TableInformationButton, TableToolsSurface } from './TableToolsSurface';
import { dataTableMinimumWidth, resolveDataTableColumnWidth } from './data-table-width.utils';
import { isDataTableBlockedByModal, subscribeToDataTableModalBoundary } from './data-table-modal-boundary.utils';
import { resolveDataTableModalSettingsPosition } from './data-table-settings-menu.utils';

export type { DataTableRowMode, DataTableTablePrefs } from '../../hooks/useDataTableUi';

const DATA_TABLE_FLOATING_EDGE_GAP = 12;
const DATA_TABLE_ROW_CONTEXT_MENU_WIDTH = 292;
const DATA_TABLE_ROW_CONTEXT_MENU_MAX_HEIGHT = 320;

function resolveDataTableRowContextMenuPosition(
    clientX: number,
    clientY: number,
): CSSProperties {
    const viewportWidth = typeof window === 'undefined' ? 320 : window.innerWidth;
    const viewportHeight = typeof window === 'undefined' ? 480 : window.innerHeight;
    const safeArea = readQubeProFloatingSafeArea();
    const minLeft = safeArea.left + DATA_TABLE_FLOATING_EDGE_GAP;
    const maxLeft = Math.max(
        minLeft,
        viewportWidth
            - safeArea.right
            - DATA_TABLE_ROW_CONTEXT_MENU_WIDTH
            - DATA_TABLE_FLOATING_EDGE_GAP,
    );
    const maxTop = Math.max(
        DATA_TABLE_FLOATING_EDGE_GAP,
        viewportHeight
            - DATA_TABLE_ROW_CONTEXT_MENU_MAX_HEIGHT
            - DATA_TABLE_FLOATING_EDGE_GAP,
    );
    return {
        left: `${Math.max(minLeft, Math.min(clientX, maxLeft))}px`,
        top: `${Math.max(DATA_TABLE_FLOATING_EDGE_GAP, Math.min(clientY, maxTop))}px`,
    };
}

const DEFAULT_DATA_TABLE_LABELS = {
    rowModeLabel: 'Row display',
    rowModeEllipsis: 'Truncate (...)',
    rowModeScroll: 'Single line + horizontal scroll',
    rowModeWrap: 'Wrap lines',
    columnsLabel: 'Columns',
    reorderColumnHint: 'Drag the handle to move this column',
    resetThisTablePrefs: 'Reset this table',
    resetThisTablePrefsTitle:
        'Clear saved layout for this table only (row mode, columns, order, widths). Does not change your global user settings.',
    dataOperationsLabel: 'Data operations',
    operationExportExcel: 'Export Excel',
    operationExportCsv: 'Export CSV',
    operationExportPdf: 'Export PDF',
    operationCorporateOutput: 'Corporate output',
    operationCorporateOutputDescription: 'Choose scope, format, language and a published corporate design.',
    bulkDataTransferLabel: 'Bulk data transfer',
    bulkDataTransferDescription: 'Templates, imports, history and field helpers.',
    operationDownloadTemplate: 'Download template',
    operationUploadTemplate: 'Upload template',
    operationImportHistory: 'Import history',
    operationFieldDictionary: 'Field dictionary',
    operationUnsupported: 'This table does not support this operation.',
    operationNoPermission: 'You do not have permission for this operation.',
    operationNotReady: 'This operation is not ready yet.',
    operationNoRows: 'There are no visible rows to export.',
    importModalTitle: 'Upload template',
    importChooseFile: 'Choose CSV file',
    importChooseFileHint: 'Upload the CSV template downloaded from this table.',
    importNoFile: 'No file selected.',
    importSummary: 'Import summary',
    importReadyRows: 'Ready rows',
    importErrorRows: 'Error rows',
    importWarnings: 'Warnings',
    importUnknownHeaders: 'Unknown headers',
    importMissingRequiredHeaders: 'Missing required headers',
    importPreview: 'Preview',
    importSubmit: 'Start import',
    importCancel: 'Cancel',
    importParsing: 'Reading file...',
    importSubmitting: 'Importing...',
    importNoValidRows: 'There are no valid rows to import.',
    importFailed: 'Import failed.',
    importSucceeded: 'Import completed.',
    importUnknownHeader: 'Unknown column: {header}',
    importMissingRequiredHeader: 'Missing required column: {header}',
    importRequiredCell: '{header} is required.',
    importMaxRowsExceeded:
        'The file has more than {maxRows} rows; only the first {maxRows} rows were evaluated.',
    refreshLabel: 'Refresh',
    focusModeEnterLabel: 'Focus table',
    focusModeFullscreenLabel: 'Enter full screen',
    focusModeExitLabel: 'Exit table focus',
    horizontalScrollLabel: 'Scroll table columns',
    selectionModeLabel: 'Select rows',
    selectAllRowsLabel: 'Select all rows',
    selectRowLabel: 'Select row',
    bulkSelectedLabel: '{count} selected',
    bulkActionsLabel: 'Bulk actions',
    tableSettingsLabel: 'Table settings',
    appearanceSectionLabel: 'Appearance',
    columnsSectionLabel: 'Columns',
    customFieldColumnsSectionLabel: 'Custom fields',
    rowActionsLabel: 'Row actions',
    rowContextMenuLabel: 'Record operations',
    rowCorporateOutputLabel: 'Create corporate output',
    rowCorporateOutputDescription: 'Create an output for this record only.',
    rowMoreActionsLabel: 'More record operations',
};

const TR_DATA_TABLE_LABELS: Partial<typeof DEFAULT_DATA_TABLE_LABELS> = {
    operationExportExcel: 'Excel indir',
    operationExportCsv: 'CSV indir',
    operationExportPdf: 'PDF dışa aktar',
    operationCorporateOutput: 'Kurumsal çıktı',
    operationCorporateOutputDescription: 'Kapsam, format, dil ve yayınlanmış kurumsal tasarımı seçin.',
    bulkDataTransferLabel: 'Toplu veri aktarımı',
    bulkDataTransferDescription: 'Şablonla içe aktarma ve alan yardımcılarını birlikte yönetin.',
    operationNoRows: 'Dışa aktarılacak görünür satır yok.',
    importModalTitle: 'Şablon yükle',
    importChooseFile: 'CSV dosyası seç',
    importChooseFileHint: 'Bu tablodan indirilen şablonu doldurup yükleyin.',
    importNoFile: 'Dosya seçilmedi.',
    importSummary: 'Yükleme özeti',
    importReadyRows: 'Hazır satır',
    importErrorRows: 'Hatalı satır',
    importWarnings: 'Uyarılar',
    importUnknownHeaders: 'Bilinmeyen kolonlar',
    importMissingRequiredHeaders: 'Eksik zorunlu kolonlar',
    importPreview: 'Ön izleme',
    importSubmit: 'Yüklemeyi başlat',
    importCancel: 'Vazgeç',
    importParsing: 'Dosya okunuyor...',
    importSubmitting: 'Yükleniyor...',
    importNoValidRows: 'Yüklenecek geçerli satır yok.',
    importFailed: 'Yükleme başarısız.',
    importSucceeded: 'Yükleme tamamlandı.',
    importUnknownHeader: 'Bilinmeyen kolon: {header}',
    importMissingRequiredHeader: 'Zorunlu kolon eksik: {header}',
    importRequiredCell: '{header} zorunlu.',
    importMaxRowsExceeded:
        'Dosyada {maxRows} satırdan fazlası var; ilk {maxRows} satır değerlendirildi.',
    refreshLabel: 'Yenile',
    focusModeEnterLabel: 'Tabloya odaklan',
    focusModeFullscreenLabel: 'Tam ekrana ge\u00e7',
    focusModeExitLabel: 'Tablo oda\u011f\u0131ndan \u00e7\u0131k',
    selectionModeLabel: 'Satır seçimi',
    selectAllRowsLabel: 'Tüm satırları seç',
    selectRowLabel: 'Satırı seç',
    bulkSelectedLabel: '{count} seçili',
    bulkActionsLabel: 'Toplu işlemler',
    tableSettingsLabel: 'Tablo ayarları',
    appearanceSectionLabel: 'Görünüm',
    columnsSectionLabel: 'Sütunlar',
    customFieldColumnsSectionLabel: 'Ek Alanlar',
    rowActionsLabel: 'Satır işlemleri',
    rowContextMenuLabel: 'Kayıt işlemleri',
    rowCorporateOutputLabel: 'Kurumsal çıktı al',
    rowCorporateOutputDescription: 'Yalnız bu kayıt için kurumsal çıktı oluşturun.',
    rowMoreActionsLabel: 'Diğer kayıt işlemleri',
};

const SENSITIVE_EXPORT_VALUE_PATTERN = /(?:postgres(?:ql)?|mysql|mssql|mongodb(?:\+srv)?|redis):\/\/[^\s]+/giu;
const SENSITIVE_EXPORT_ASSIGNMENT_PATTERN = /\b(password|passwd|pwd|secret|token|api[-_ ]?key|authorization|credential)\s*[:=]\s*[^\s,;]+/giu;
const SENSITIVE_EXPORT_BEARER_PATTERN = /\bBearer\s+[A-Za-z0-9._~+\/-]+=*/giu;
const SENSITIVE_EXPORT_JWT_PATTERN = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/gu;

function getDefaultDataTableLabels(language: SupportedLanguage) {
    return language === 'tr'
        ? { ...DEFAULT_DATA_TABLE_LABELS, ...TR_DATA_TABLE_LABELS }
        : DEFAULT_DATA_TABLE_LABELS;
}

export type DataTableOperationKey =
    | 'corporateOutput'
    | 'exportExcel'
    | 'exportCsv'
    | 'exportPdf'
    | 'downloadTemplate'
    | 'uploadTemplate'
    | 'importHistory'
    | 'fieldDictionary';

export interface DataTableOperationConfig {
    /** Tablo/domain bu operasyonu destekliyor mu? `false` ise yetki olsa bile pasif kalır. */
    supported?: boolean;
    /** Operasyon için gereken RBAC kodu (`module:action`). Boşsa yalnızca capability/handler kontrol edilir. */
    permission?: string;
    label?: string;
    description?: string;
    disabledReason?: string;
    onClick?: () => void | Promise<void>;
    /** Standart şablon yükleme motoru için kolon sözlüğü. Genelde indirme şablonu ile aynı olmalı. */
    importColumns?: DataTableCsvColumn[];
    importOptions?: DataTableImportOptions;
    onImport?: (
        payload: DataTableImportPayload,
    ) => void | DataTableImportSubmitResult | Promise<void | DataTableImportSubmitResult>;
    visible?: boolean;
}

export type DataTableDataOperations = Partial<Record<DataTableOperationKey, DataTableOperationConfig>>;

interface ResolvedDataTableOperation {
    key: DataTableOperationKey;
    label: string;
    description?: string;
    icon: string;
    disabled: boolean;
    onClick?: () => void | Promise<void>;
}

const DATA_TABLE_OPERATION_ORDER: DataTableOperationKey[] = [
    'corporateOutput',
    'downloadTemplate',
    'uploadTemplate',
    'importHistory',
    'fieldDictionary',
];

const DATA_TABLE_OPERATION_ICONS: Record<DataTableOperationKey, string> = {
    corporateOutput: 'file-text',
    exportExcel: 'download',
    exportCsv: 'download',
    exportPdf: 'file-text',
    downloadTemplate: 'download',
    uploadTemplate: 'upload',
    importHistory: 'schedule',
    fieldDictionary: 'info',
};

// Sütun tanımı arayüzü
export type DataTableColumnOutputType = 'AUTO' | DataTableOutputColumnType;

export interface DataTableColumnOutputMetadata<T> {
    type?: DataTableColumnOutputType;
    value?: (item: T) => unknown;
    align?: 'left' | 'center' | 'right';
    widthHint?: 'NARROW' | 'NORMAL' | 'WIDE';
    wrap?: boolean;
    keyColumn?: boolean;
    format?: DataTableOutputColumnFormat;
}

export interface Column<T> {
    /** Veri alanı veya yalnızca son işlemler sütunu için `'actions'`. `'actions'` iki kez kullanılmamalı — tercih genişliği ve `actions-col` çakışır. */
    key: keyof T | 'actions' | string;
    title: ReactNode;
    sortable?: boolean;
    width?: string;
    /** Minimum usable column width in CSS pixels. Horizontal scrolling preserves it. */
    minWidth?: number;
    /** Keep one identity column visible at the inline start while scrolling. */
    pinned?: 'start';
    align?: 'left' | 'center' | 'right';
    render?: (item: T) => ReactNode; // Özel hücre render fonksiyonu
    filterable?: boolean; // Sütun bazlı filtreleme var mı?
    // Sütun bazlı filtreleme için state ve setState fonksiyonları dışarıdan verilmeli (şimdilik opsiyonel)
    filterValue?: string;
    onFilterChange?: (value: string) => void;
    filterType?: 'text' | 'select'; // Filtre tipi
    filterOptions?: { label: string; value: string }[]; // Select filtresi için seçenekler
    /** Özel sütun filtresi (örn. renkli durum rozeti); verilirse filterType/select yerine kullanılır */
    renderFilter?: () => ReactNode;
    /** user-settings içindeki sütun kimliği (varsayılan: String(key)) */
    preferenceId?: string;
    /** Kolon ayarlarında ayraç altında gruplamak için kullanılır. */
    settingsGroup?: 'customFields' | string;
    /** preferencesKey kullanıldığında ilk açılışta gizli */
    defaultHidden?: boolean;
    /**
     * Kısalt (ellipsis) modunda tam metin için `td title` (native tooltip).
     * `render` kullanıldığında, görünen kısaltılmış metnin tamamını burada verin.
     */
    getTitle?: (item: T) => string | undefined;
    /** `false` bu kolonu çıktıda bilinçli olarak dışlar; metadata tipli snapshot üretimini yönetir. */
    output?: false | DataTableColumnOutputMetadata<T>;
}

export interface DataTableBulkActionContext<T> {
    selectedIds: Array<string | number>;
    selectedRows: T[];
    visibleRowIds: Array<string | number>;
    clearSelection: () => void;
}

export interface DataTableBulkAction<T> {
    key: string;
    label: string;
    icon?: string;
    title?: string;
    variant?: 'secondary' | 'danger' | 'primary';
    hidden?: boolean | ((context: DataTableBulkActionContext<T>) => boolean);
    disabled?: boolean | ((context: DataTableBulkActionContext<T>) => boolean);
    onClick: (context: DataTableBulkActionContext<T>) => void | Promise<void>;
}

type DataTableSmartFilterConfig = Omit<SmartFilterProps, 'identity' | 'language'> & {
    identity?: QubeProDataTableIdentity;
    language?: SupportedLanguage;
};

export interface DataTableRowAction<T> {
    key: string;
    label: string;
    icon?: string;
    buttonClass?: string;
    title?: string;
    ariaLabel?: string;
    permission?: string;
    disabled?: boolean | ((item: T, rowIndex: number) => boolean);
    hidden?: boolean | ((item: T, rowIndex: number) => boolean);
    disabledReason?: string;
    onSelect: (item: T, rowIndex: number) => void | Promise<void>;
}

interface DataTableRowActionContext {
    itemIndex: number;
}

export interface DataTableRowActions<T> {
    render?: (item: T) => ReactNode;
    items?: Array<DataTableRowAction<T>>;
    ariaLabel?: string;
}

export interface DataTableAllFilteredOutputContext {
    tableId: string;
    locale: SupportedLanguage;
    format: DataTableOutputFormat;
    templateId: string;
    timeZone: string;
    dateFormat: string;
    timeFormat: '12h' | '24h';
    title?: string;
    fileName?: string;
    filters: Array<{ label: string; value: string }>;
    quickSearch?: string;
    sort?: { label: string; direction: 'asc' | 'desc' };
    retry: boolean;
}

interface DataTableOutputExportConfigBase {
    /** Boşsa sayfadaki ana başlık kullanılır. */
    title?: string;
    /** Boşsa başlık ve zaman damgasından güvenli bir PDF adı üretilir. */
    fileName?: string;
    /** Sayfanın DataTable dışında uyguladığı filtreler için kısa açıklama. */
    filterSummary?: string;
    /** Sistem geneli raporlarda 0, firma raporlarında yetkili firma kimliği. */
    companyScopeId?: string;
    /** Büyük sunucu snapshot'ı şirket tasarımlarından bağımsız kilitli Output tasarımını kullanır. */
    allFilteredTemplatePolicy?: 'SYSTEM_MANAGED';
}

export type DataTableOutputExportConfig = DataTableOutputExportConfigBase & (
    | {
        defaultScope?: 'VISIBLE_ROWS';
        queueAllFilteredOutput?: never;
    }
    | {
        defaultScope: 'ALL_FILTERED_ROWS';
        /**
         * Ürün-local server endpoint'i permission/tenant katmanından snapshot hazırlayıp işi kuyruğa alır.
         * Browser'a tam veri seti, Output tokenı veya DMS referansı dönmez; yalnız standart job cevabı döner.
         */
        queueAllFilteredOutput: (context: DataTableAllFilteredOutputContext) => Promise<unknown>;
    }
);

export type DataTableOutputNotApplicableReasonCode =
    | 'SECURITY_RESTRICTED'
    | 'REFERENCE_CATALOG'
    | 'INTERACTIVE_PICKER';

export interface DataTableOutputNotApplicablePolicy {
    state: 'NOT_APPLICABLE';
    reasonCode: DataTableOutputNotApplicableReasonCode;
    /** Kalıcı audit gerekçesidir; son kullanıcı arayüzünde gösterilmez. */
    reason: string;
}

export type DataTableOutputPolicy =
    | DataTableOutputExportConfig
    | DataTableOutputNotApplicablePolicy;

function isDataTableOutputNotApplicablePolicy(
    value: DataTableOutputPolicy | undefined,
): value is DataTableOutputNotApplicablePolicy {
    return Boolean(value && 'state' in value && value.state === 'NOT_APPLICABLE');
}

export interface DataTableClientPaginationConfig {
    /** Form/modal icindeki sonlu veri kumesini DataTable icinde sayfalar. */
    mode: 'client';
    /** Kurumsal 5/20/50/100 seceneklerinden biri; gecersiz deger 20'ye normalize edilir. */
    initialPageSize?: number;
}

export interface DataTableClientPagePaginationConfig {
    /** Sonlu ana listeyi istemcide sayfalar; Pagination'i DataTable'in sayfa kardesi olarak render eder. */
    mode: 'client-page';
    /** Kurumsal 5/20/50/100 seceneklerinden biri; gecersiz deger 20'ye normalize edilir. */
    initialPageSize?: number;
}

export type DataTablePaginationConfig =
    | DataTableClientPaginationConfig
    | DataTableClientPagePaginationConfig;

export type DataTableInteractionMode = 'browse' | 'edit';

type DataTableRefreshContract =
    | {
        /** Kurumsal toolbar yenile butonu için sayfanın veri yenileme handler'ı. */
        onRefresh: () => void | Promise<void>;
        hideRefresh?: false;
    }
    | {
        /** Yalnız harici veri kaynağı olmayan statik/nested tablolar için bilinçli istisna. */
        hideRefresh: true;
        onRefresh?: never;
    };

type DataTableProps<T> = {
    data: T[];
    columns: Column<T>[];
    loading?: boolean;
    emptyMessage?: string;
    sortColumn?: keyof T | string;
    sortDirection?: 'asc' | 'desc';
    onSort?: (column: keyof T) => void;
    isSortManuallyChanged?: boolean; // Sıralama kullanıcı tarafından mı değiştirildi (görsel için)
    language?: SupportedLanguage;
    onRowClick?: (item: T) => void;
    /** Satır üzerinde sağ tık (bağlam menüsü) */
    onRowContextMenu?: (e: MouseEvent<HTMLTableRowElement>, item: T) => void;
    getRowClassName?: (item: T) => string | undefined;
    selectedRowIds?: Set<string | number>;
    onToggleRowSelection?: (id: string | number, checked: boolean) => void;
    onToggleAllRowSelection?: (ids: Array<string | number>, checked: boolean) => void;
    rowSelectionLabel?: string;
    /** Secili satirlar icin tabloya ozel toplu islem aksiyonlari. */
    bulkActions?: Array<DataTableBulkAction<T>>;
    /**
     * Kalıcı tablo UI (`userSettings.dataTableUi.tables` alt anahtarı).
     * Verilmezse DataTable route + sütun imzasından ürün bazlı güvenli bir anahtar üretir.
     * Kurumsal yeni kullanım: `tableIdentity={{ module: 'customer', page: 'customers', component: 'main' }}`.
     * Eski `preferencesKey={dataTableStorageTableId('companies')}` kullanımı geriye uyumluluk için korunur.
     */
    preferencesKey?: string;
    /** `APP.module.page.table.component` formatında kurumsal tablo kimliği üretir. */
    tableIdentity?: QubeProDataTableIdentity;
    /** preferencesKey veya sunucu ayarı yokken kullanılacak satır modu */
    rowMode?: DataTableRowMode;
    /** Uzun veri tablolarinda baslik ve filtre satirini tablo scroll alaninda sabit tutar. */
    stickyHeader?: boolean;
    /**
     * Ürün açıkça etkinleştirdiğinde, `actions` dışındaki kolonlarda aksi belirtilmedikçe
     * sıralama ve filtreleme kontrollerini açar. Harici handler verilmeyen tablolar
     * yüklenmiş satırlar üzerinde güvenli, yerel sıralama/filtreleme kullanır.
     */
    /** Tüm veri kolonlarında varsayılan filtre/sıralamayı açar; yalnız gerçek istisnalarda `false` verilir. */
    defaultColumnControls?: boolean;
    /**
     * `edit`, belge alt satırı gibi veri giriş yüzeylerinde ana-liste
     * seçim/odak/yenile/ayar araçlarını gizler. Satır iş kurallarını,
     * kolon renderer'larını veya persistence davranışını değiştirmez.
     */
    interactionMode?: DataTableInteractionMode;
    /** Disable the table's own view levels when a parent modal owns this surface. */
    workspaceFocusEnabled?: boolean;
    /** Kurumsal akilli filtreyi DataTable toolbar'ina standart bicimde baglar. */
    smartFilter?: DataTableSmartFilterConfig;
    /** Kurumsal toolbar sol tarafina sayfa ozel ek aksiyonlar yerlestirir. */
    toolbarLeadingContent?: ReactNode;
    /** Optional guidance uses the corporate information icon, never a second panel. */
    information?: { label: string; description: string };
    /** Manifest + kullanıcı permission kontrolüyle yönetilen kurumsal tablo veri işlemleri. */
    dataOperations?: DataTableDataOperations;
    /** Varsayılan çıktıyı özelleştirir veya gerekçeli kurumsal istisna beyan eder. */
    outputExport?: DataTableOutputPolicy;
    /**
     * `client`, form/modal icindeki sonlu veri kumelerinde sayfalamayi DataTable'in icinde tutar.
     * `client-page`, sonlu ana listede sayfalamayi kurumsal kardes `Pagination` konumunda render eder.
     * Sunucu sayfalamali liste sayfalari mevcut kontrollu kardes `Pagination` sozlesmesini kullanir.
     */
    pagination?: DataTablePaginationConfig;
    /** Sinirli form/tab panelinde tablo govdesini kalan yukseklige yayar ve sayfalamayi alta sabitler. */
    fillAvailableHeight?: boolean;
    /** Modal tab paneli gibi kendi kabuğu olan yüzeylerde ikinci tablo kartını kaldırır. */
    inlineSurface?: 'card' | 'flat';
    rowActions?: DataTableRowActions<T> | false;
} & DataTableRefreshContract;

function shouldIgnoreRowClick(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return Boolean(
        target.closest(
            'button, a, input, select, textarea, label, [role="button"], [role="link"], [role="checkbox"], [data-no-row-click="true"]',
        ),
    );
}

/** input placeholder yalnızca string; başlık JSX olabilir */
function joinClassNames(...classes: Array<string | false | null | undefined>): string | undefined {
    const value = classes.filter(Boolean).join(' ');
    return value || undefined;
}

function getAdjacentPaginationShell(wrapper: HTMLElement): HTMLElement | null {
    const shell = wrapper.closest('.datatable-shell');
    const owned = shell?.querySelector(':scope > .pagination-shell');
    if (owned instanceof HTMLElement) return owned;

    const next = shell?.nextElementSibling;
    if (next instanceof HTMLElement && next.classList.contains('pagination-shell')) return next;

    const pageContainer = shell?.closest('.page-container');
    const pagePagination = pageContainer
        ? Array.from(pageContainer.children).find(
              (element) => element instanceof HTMLElement && element.classList.contains('pagination-shell'),
          )
        : null;
    return pagePagination instanceof HTMLElement ? pagePagination : null;
}

function readPixelValue(value: string | null | undefined): number {
    if (!value) return 0;
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function titleToInputPlaceholder(title: ReactNode): string | undefined {
    if (typeof title === 'string') return title;
    if (typeof title === 'number') return String(title);
    return undefined;
}

function getColumnPreferenceId<T>(c: Column<T>): string {
    return c.preferenceId ?? String(c.key);
}

function getColumnFilterAccessibleName<T>(column: Column<T>, template: string | undefined): string {
    const title = (reactNodePlainText(column.title).trim() || getColumnPreferenceId(column))
        .replace(/\s+/g, ' ');
    return (template || 'Filter by {column}').replace('{column}', title);
}

function slugDataTableKeyPart(value: string): string {
    const slug = value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 80);
    return slug || 'root';
}

function hashDataTableSignature(value: string): string {
    let hash = 2166136261;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(36);
}

function buildAutoPreferencesKey<T>(pathname: string, columns: Column<T>[]): string {
    const pathPart = slugDataTableKeyPart(pathname.replace(/^\/+/, '') || 'root');
    const columnSignature = columns
        .filter((c) => c.key !== 'actions')
        .map(getColumnPreferenceId)
        .join('|');
    const signaturePart = hashDataTableSignature(columnSignature || 'default');
    return dataTableStorageTableId(`auto.${pathPart}.${signaturePart}`);
}

function getCurrentPathname(): string {
    if (typeof window === 'undefined') return 'root';
    return window.location?.pathname || 'root';
}

function getLocalizedTableText(
    value: Record<string, string> | undefined,
    language: SupportedLanguage,
    fallback?: string,
): string | undefined {
    if (!value) return fallback;
    const locale = String(language || DEFAULT_LANGUAGE).toLowerCase();
    return value[locale] ?? value.tr ?? value.en ?? fallback;
}

function getOperationFallbackLabel(
    key: DataTableOperationKey,
    labels: typeof DEFAULT_DATA_TABLE_LABELS & Record<string, string>,
): string {
    switch (key) {
        case 'corporateOutput':
            return labels.operationCorporateOutput;
        case 'exportExcel':
            return labels.operationExportExcel;
        case 'exportCsv':
            return labels.operationExportCsv;
        case 'exportPdf':
            return labels.operationExportPdf;
        case 'downloadTemplate':
            return labels.operationDownloadTemplate;
        case 'uploadTemplate':
            return labels.operationUploadTemplate;
        case 'importHistory':
            return labels.operationImportHistory;
        case 'fieldDictionary':
            return labels.operationFieldDictionary;
        default:
            return key;
    }
}

function indexManifestOperations(
    operations: TableOperationManifestItem[] | undefined,
): Map<DataTableOperationKey, TableOperationManifestItem> {
    const result = new Map<DataTableOperationKey, TableOperationManifestItem>();
    if (!Array.isArray(operations)) return result;
    for (const operation of operations) {
        if (DATA_TABLE_OPERATION_ORDER.includes(operation.key)) {
            result.set(operation.key, operation);
        }
    }
    return result;
}

/** Kayıtlı `columnOrder` ile görünür veri sütunlarını sıralar; bilinmeyen id atlanır, eksikler kod sırasıyla eklenir. */
function orderVisibleDataColumns<T>(
    columns: Column<T>[],
    hiddenIds: Set<string>,
    savedOrder: string[] | undefined,
): Column<T>[] {
    const dataCols = columns.filter((c) => c.key !== 'actions');
    const visibleData = dataCols.filter((c) => !hiddenIds.has(getColumnPreferenceId(c)));
    if (!savedOrder?.length) {
        return visibleData;
    }
    const byId = new Map(visibleData.map((c) => [getColumnPreferenceId(c), c] as const));
    const used = new Set<string>();
    const ordered: Column<T>[] = [];
    for (const id of savedOrder) {
        const col = byId.get(id);
        if (col) {
            ordered.push(col);
            used.add(id);
        }
    }
    for (const c of dataCols) {
        const id = getColumnPreferenceId(c);
        if (hiddenIds.has(id)) continue;
        if (!used.has(id)) ordered.push(c);
    }
    return ordered;
}

/** Tüm veri sütun id'leri (gizli dahil): kayıtlı sıra + tanımda yeni olanlar. */
function mergeFullDataColumnOrder<T>(cols: Column<T>[], savedOrder: string[] | undefined): string[] {
    const dataCols = cols.filter((c) => c.key !== 'actions');
    const allIds = dataCols.map(getColumnPreferenceId);
    if (!savedOrder?.length) {
        return [...allIds];
    }
    const allowed = new Set(allIds);
    const ordered: string[] = [];
    const used = new Set<string>();
    for (const id of savedOrder) {
        if (!allowed.has(id) || used.has(id)) continue;
        ordered.push(id);
        used.add(id);
    }
    for (const id of allIds) {
        if (!used.has(id)) ordered.push(id);
    }
    return ordered;
}

function reorderColumnIdsInFullList(ids: string[], dragId: string, dropId: string): string[] {
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(dropId);
    if (from < 0 || to < 0 || dragId === dropId) {
        return ids;
    }
    const next = [...ids];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    return next;
}

function computeHiddenIds<T>(cols: Column<T>[], tablePrefs: DataTableTablePrefs | undefined): Set<string> {
    const dataCols = cols.filter((c) => c.key !== 'actions');
    const ids = dataCols.map(getColumnPreferenceId);
    if (tablePrefs && tablePrefs.hiddenColumns !== undefined) {
        const hidden = new Set(tablePrefs.hiddenColumns.filter((h) => ids.includes(h)));
        const knownOrder = new Set(tablePrefs.columnOrder ?? []);
        for (const column of dataCols) {
            const id = getColumnPreferenceId(column);
            if (column.defaultHidden && !knownOrder.has(id)) hidden.add(id);
        }
        return hidden;
    }
    return new Set(dataCols.filter((c) => c.defaultHidden).map(getColumnPreferenceId));
}

function formatCellPlainText(value: unknown): string {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    try {
        return JSON.stringify(value);
    } catch {
        return '';
    }
}

function getDataTableExportHeader<T>(column: Column<T>): string {
    const fromTitle = titleToInputPlaceholder(column.title);
    return fromTitle?.trim() || getColumnPreferenceId(column);
}

function isSensitiveDataTableExportColumn<T>(column: Column<T>): boolean {
    return [getColumnPreferenceId(column), getDataTableExportHeader(column)]
        .some(isSensitiveDataTableOutputIdentifier);
}

function sanitizeDataTableExportValue(value: string): string {
    return value
        .replace(SENSITIVE_EXPORT_VALUE_PATTERN, '[GİZLENDİ]')
        .replace(SENSITIVE_EXPORT_ASSIGNMENT_PATTERN, '$1=[GİZLENDİ]')
        .replace(SENSITIVE_EXPORT_BEARER_PATTERN, 'Bearer [GİZLENDİ]')
        .replace(SENSITIVE_EXPORT_JWT_PATTERN, '[GİZLENDİ]');
}

function reactNodePlainText(node: ReactNode): string {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(reactNodePlainText).filter(Boolean).join(' ').trim();
    if (!isValidElement(node)) return '';
    const props = node.props as {
        children?: ReactNode;
        title?: unknown;
        'aria-label'?: unknown;
    };
    const childText = reactNodePlainText(props.children);
    if (childText) return childText;
    if (typeof props['aria-label'] === 'string') return props['aria-label'];
    if (typeof props.title === 'string') return props.title;
    return '';
}

function getDataTableExportValue<T>(column: Column<T>, item: T): string {
    const fromGetter = column.getTitle?.(item);
    if (fromGetter !== undefined && fromGetter !== null) return sanitizeDataTableExportValue(fromGetter);
    if (column.render) {
        const rendered = reactNodePlainText(column.render(item));
        if (rendered) return sanitizeDataTableExportValue(rendered);
    }
    return sanitizeDataTableExportValue(formatCellPlainText((item as Record<string, unknown>)[String(column.key)]));
}

function getDataTableOutputRawValue<T>(column: Column<T>, item: T): unknown {
    if (column.output && column.output.value) return column.output.value(item);
    const fromGetter = column.getTitle?.(item);
    if (fromGetter !== undefined && fromGetter !== null) return sanitizeDataTableExportValue(fromGetter);
    if (column.render) {
        const rendered = reactNodePlainText(column.render(item));
        if (rendered) return sanitizeDataTableExportValue(rendered);
    }
    const raw = (item as Record<string, unknown>)[String(column.key)];
    if (
        raw === null
        || raw === undefined
        || raw instanceof Date
        || typeof raw === 'string'
        || typeof raw === 'number'
        || typeof raw === 'boolean'
    ) return raw;
    throw new Error('DataTable çıktı hücresi yalnız primitive değer içerebilir; özel görünüm için output.value tanımlayın.');
}

function isStrictIsoDate(value: string): boolean {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
    const parsed = new Date(`${value}T00:00:00.000Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

function isStrictIsoDateTime(value: string): boolean {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?(Z|[+-]\d{2}:\d{2})$/.exec(value);
    if (!match) return false;
    const [, year, month, day, hour, minute, second, milliseconds = '0', offset] = match;
    if (!isStrictIsoDate(`${year}-${month}-${day}`)) return false;
    if (Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59) return false;
    if (offset !== 'Z') {
        const offsetMatch = /^([+-]\d{2}):(\d{2})$/.exec(offset);
        if (!offsetMatch || Number(offsetMatch[1].slice(1)) > 23 || Number(offsetMatch[2]) > 59) return false;
    }
    return !Number.isNaN(Date.parse(
        `${year}-${month}-${day}T${hour}:${minute}:${second}.${milliseconds.padEnd(3, '0')}${offset}`,
    ));
}

function inferDataTableColumnOutputType<T>(column: Column<T>, values: unknown[]): DataTableOutputColumnType {
    const declared = column.output && column.output.type;
    if (declared && declared !== 'AUTO') return declared;
    const populated = values.filter((value) => value !== null && value !== undefined);
    if (populated.length > 0 && populated.every((value) => typeof value === 'boolean')) return 'BOOLEAN';
    if (populated.length > 0 && populated.every((value) => typeof value === 'number' && Number.isFinite(value))) {
        return populated.every((value) => Number.isInteger(value)) ? 'INTEGER' : 'DECIMAL';
    }
    if (populated.length > 0 && populated.every((value) => value instanceof Date)) return 'DATETIME';
    if (populated.length > 0 && populated.every((value) => typeof value === 'string' && isStrictIsoDate(value))) return 'DATE';
    if (populated.length > 0 && populated.every((value) => typeof value === 'string' && isStrictIsoDateTime(value))) return 'DATETIME';
    if (
        populated.length > 0
        && populated.every((value) => typeof value === 'string')
        && /(?:^|[._-])(code|number|no)(?:$|[._-])/i.test(getColumnPreferenceId(column))
    ) return 'CODE';
    return 'TEXT';
}

function normalizeDataTableOutputCell(
    value: unknown,
    type: DataTableOutputColumnType,
    explicitType: boolean,
): DataTableOutputCell {
    if (value === null || value === undefined) return null;
    if (type === 'BOOLEAN') {
        if (typeof value === 'boolean') return value;
        throw new Error('BOOLEAN DataTable çıktı değeri boolean olmalıdır.');
    }
    if (type === 'INTEGER') {
        if (typeof value === 'number' && Number.isSafeInteger(value)) return value;
        throw new Error('INTEGER DataTable çıktı değeri güvenli tam sayı olmalıdır.');
    }
    if (type === 'DECIMAL' || type === 'CURRENCY' || type === 'PERCENT') {
        if (typeof value === 'number' && Number.isFinite(value)) return value;
        throw new Error(`${type} DataTable çıktı değeri sonlu sayı olmalıdır.`);
    }
    if (type === 'DATE') {
        if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString().slice(0, 10);
        if (typeof value === 'string' && isStrictIsoDate(value)) return value;
        throw new Error('DATE DataTable çıktı değeri YYYY-MM-DD olmalıdır.');
    }
    if (type === 'DATETIME') {
        if (value instanceof Date && !Number.isNaN(value.getTime())) return value.toISOString();
        if (typeof value === 'string' && isStrictIsoDateTime(value)) return value;
        throw new Error('DATETIME DataTable çıktı değeri timezone içeren ISO-8601 olmalıdır.');
    }
    if (explicitType && typeof value !== 'string') {
        throw new Error(`${type} DataTable çıktı değeri string olmalıdır.`);
    }
    const text = sanitizeDataTableExportValue(formatCellPlainText(value));
    if (text.length > 500) throw new Error(`${type} DataTable çıktı değeri 500 karakteri aşamaz.`);
    return text;
}

function buildSmartDataTableOutputDataset<T>(columns: Column<T>[], rows: T[]): {
    columns: DataTableOutputColumn[];
    rows: DataTableOutputCell[][];
} {
    const keys = new Set<string>();
    let keyColumnCount = 0;
    const prepared = columns.map((column) => {
        const key = getColumnPreferenceId(column).trim();
        if (!key || keys.has(key)) throw new Error(`DataTable çıktı kolon anahtarı benzersiz olmalıdır: ${key || '(boş)'}`);
        keys.add(key);
        const rawValues = rows.map((row) => getDataTableOutputRawValue(column, row));
        const type = inferDataTableColumnOutputType(column, rawValues);
        const metadata = column.output || undefined;
        if (metadata?.keyColumn) keyColumnCount += 1;
        const outputColumn: DataTableOutputColumn = {
            key,
            label: getDataTableExportHeader(column),
            type,
            ...(metadata?.align || column.align ? { align: metadata?.align ?? column.align } : {}),
            ...(metadata?.widthHint ? { widthHint: metadata.widthHint } : {}),
            ...(metadata?.wrap !== undefined ? { wrap: metadata.wrap } : {}),
            ...(metadata?.keyColumn !== undefined ? { keyColumn: metadata.keyColumn } : {}),
            ...(metadata?.format ? { format: metadata.format } : {}),
        };
        return { outputColumn, rawValues, explicitType: Boolean(metadata?.type && metadata.type !== 'AUTO') };
    });
    if (keyColumnCount > 2) throw new Error('DataTable çıktısında en fazla iki keyColumn tanımlanabilir.');
    return {
        columns: prepared.map((item) => item.outputColumn),
        rows: rows.map((_row, rowIndex) => prepared.map((item) => (
            normalizeDataTableOutputCell(item.rawValues[rowIndex], item.outputColumn.type, item.explicitType)
        ))),
    };
}

function resolveBrowserTimeZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
        return 'UTC';
    }
}

function DefaultTableCell({ value }: { value: unknown }) {
    if (value === null || value === undefined) {
        return <span className="datatable-cell-placeholder">—</span>;
    }
    const str = formatCellPlainText(value);
    return <span className="datatable-cell-inner">{str}</span>;
}

function ellipsisTdTitle<T>(
    effectiveRowMode: DataTableRowMode,
    column: Column<T>,
    item: T,
): string | undefined {
    if (effectiveRowMode !== 'ellipsis' || column.key === 'actions') return undefined;
    if (column.render) {
        const fromGetter = column.getTitle?.(item);
        if (fromGetter != null) {
            const t = String(fromGetter).trim();
            if (t) return t;
        }
        return undefined;
    }
    const s = formatCellPlainText(item[column.key as keyof T]).trim();
    return s || undefined;
}

export function DataTable<T extends { id: string | number }>({
    data,
    columns,
    loading = false,
    emptyMessage,
    sortColumn,
    sortDirection = 'asc',
    onSort,
    isSortManuallyChanged = false,
    language = DEFAULT_LANGUAGE,
    onRowClick,
    onRowContextMenu,
    getRowClassName,
    selectedRowIds,
    onToggleRowSelection,
    onToggleAllRowSelection,
    rowSelectionLabel,
    bulkActions,
    preferencesKey,
    tableIdentity,
    rowMode: rowModeProp = 'ellipsis',
    stickyHeader = true,
    defaultColumnControls: defaultColumnControlsProp,
    interactionMode = 'browse',
    workspaceFocusEnabled = true,
    smartFilter,
    toolbarLeadingContent,
    information,
    onRefresh,
    hideRefresh,
    dataOperations,
    outputExport,
    pagination,
    fillAvailableHeight = false,
    inlineSurface = fillAvailableHeight && pagination?.mode === 'client' ? 'flat' : 'card',
    rowActions,
}: DataTableProps<T>) {
    const defaultColumnControls = defaultColumnControlsProp ?? (interactionMode === 'browse');
    const isEditInteraction = interactionMode === 'edit';
    const t = useTranslation(language);
    const dataTableLabels = { ...getDefaultDataTableLabels(language), ...(t.dataTable ?? {}) };
    const smartFilterIdentity = smartFilter?.identity ?? tableIdentity;
    const smartFilterNode = smartFilter && smartFilterIdentity ? (
        <SmartFilter
            {...smartFilter}
            identity={smartFilterIdentity}
            language={smartFilter.language ?? language}
        />
    ) : undefined;
    const resolvedToolbarLeadingContent =
        smartFilterNode || toolbarLeadingContent || information ? (
            <>
                {information && <TableInformationButton {...information} />}
                {smartFilterNode}
                {toolbarLeadingContent ? (
                    <DataTableToolsProvider value={{ identity: tableIdentity, language }}>
                        {toolbarLeadingContent}
                    </DataTableToolsProvider>
                ) : null}
            </>
        ) : undefined;
    const effectivePreferencesKey = useMemo(
        () => preferencesKey?.trim() || (tableIdentity ? dataTableStorageTableId(tableIdentity) : buildAutoPreferencesKey(getCurrentPathname(), columns)),
        [columns, preferencesKey, tableIdentity],
    );
    const { hasPermission } = usePermission();
    const outputDateTime = useDateTime();
    const manifestTableOperations = useTableOperationManifest(effectivePreferencesKey);
    const prefs = useDataTableUi({
        preferencesKey: effectivePreferencesKey,
        fallbackRowMode: rowModeProp,
    });
    const effectiveRowMode = prefs.effectiveRowMode;

    const [columnMenuOpen, setColumnMenuOpen] = useState(false);
    const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
    const [bulkDataTransferOpen, setBulkDataTransferOpen] = useState(false);
    const [importModalOpen, setImportModalOpen] = useState(false);
    const [importPayload, setImportPayload] = useState<DataTableImportPayload | null>(null);
    const [importParseResult, setImportParseResult] = useState<DataTableImportParseResult | null>(null);
    const [importError, setImportError] = useState<string | null>(null);
    const [importMessage, setImportMessage] = useState<string | null>(null);
    const [importParsing, setImportParsing] = useState(false);
    const [importSubmitting, setImportSubmitting] = useState(false);
    const [selectionColumnActive, setSelectionColumnActive] = useState(false);
    const [internalSelectedRowIds, setInternalSelectedRowIds] = useState<Set<string | number>>(() => new Set());
    const [internalColumnFilters, setInternalColumnFilters] = useState<Record<string, string>>({});
    const [internalSortColumn, setInternalSortColumn] = useState<string | null>(null);
    const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>('asc');
    const [clientPage, setClientPage] = useState(1);
    const [clientPageSize, setClientPageSize] = useState(() =>
        normalizePageSize(pagination?.initialPageSize ?? DEFAULT_PAGE_SIZE),
    );
    const generatedImportFormId = useId();
    const importFormId = `datatable-import-${generatedImportFormId.replace(/:/g, '')}`;
    const shellRef = useRef<HTMLDivElement | null>(null);
    const toolbarRef = useRef<HTMLDivElement | null>(null);
    const settingsButtonRef = useRef<HTMLButtonElement | null>(null);
    const settingsMenuRef = useRef<HTMLDivElement | null>(null);
    const columnSettingsButtonRef = useRef<HTMLButtonElement | null>(null);
    const bulkTransferButtonRef = useRef<HTMLButtonElement | null>(null);
    const tableViewportRef = useRef<HTMLDivElement | null>(null);
    const tableWrapperRef = useRef<HTMLDivElement | null>(null);
    const rowActionsOverlayRef = useRef<HTMLDivElement | null>(null);
    const rowContextMenuRef = useRef<HTMLDivElement | null>(null);
    const [dragOverColId, setDragOverColId] = useState<string | null>(null);
    const headerCheckboxRef = useRef<HTMLInputElement>(null);
    const activeRowActionElementRef = useRef<HTMLTableRowElement | null>(null);
    const restoringLineActionFocusRef = useRef(false);
    const [lineActionFocusRequest, setLineActionFocusRequest] = useState<string | number | null>(null);
    const rowActionsHideTimerRef = useRef<number | null>(null);
    const [activeRowActionId, setActiveRowActionId] = useState<string | number | null>(null);
    const [rowActionSpace, setRowActionSpace] = useState(0);
    const [rowActionsOverlayPosition, setRowActionsOverlayPosition] = useState<{
        top: number;
        right: number;
        maxWidth: number;
    } | null>(null);
    const [rowContextMenu, setRowContextMenu] = useState<{
        itemId: string | number;
        clientX: number;
        clientY: number;
    } | null>(null);
    const [settingsMenuPosition, setSettingsMenuPosition] = useState<{
        top: number;
        right: number;
        maxHeight: number;
        width?: number;
    } | null>(null);

    const updateLastDataRowDivider = useCallback(() => {
        const wrapper = tableWrapperRef.current;
        if (!wrapper) return;

        const table = wrapper.querySelector<HTMLTableElement>(':scope > table');
        const lastDataRow = table?.querySelector<HTMLTableRowElement>(
            ':scope > tbody > tr.datatable-data-row:last-child',
        );
        const hasTrailingSpace = Boolean(
            table && lastDataRow && table.offsetHeight + 1 < wrapper.clientHeight,
        );

        wrapper.classList.toggle('datatable-has-trailing-space', hasTrailingSpace);
    }, []);

    useLayoutEffect(() => {
        updateLastDataRowDivider();
    });

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        const wrapper = tableWrapperRef.current;
        const table = wrapper?.querySelector<HTMLTableElement>(':scope > table');
        if (!wrapper || !table) return undefined;

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updateLastDataRowDivider)
            : null;
        const animationFrame = window.requestAnimationFrame(updateLastDataRowDivider);

        resizeObserver?.observe(wrapper);
        resizeObserver?.observe(table);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver?.disconnect();
            wrapper.classList.remove('datatable-has-trailing-space');
        };
    }, [updateLastDataRowDivider]);

    const updateStickyHeaderHeight = useCallback(() => {
        if (!stickyHeader || typeof window === 'undefined') return;
        const viewport = tableViewportRef.current;
        const wrapper = tableWrapperRef.current;
        if (!viewport || !wrapper) return;

        const wrapperRect = wrapper.getBoundingClientRect();
        const wrapperStyle = window.getComputedStyle(wrapper);
        const headerScrollbarWidth = `${Math.max(
            0,
            wrapper.offsetWidth -
                wrapper.clientWidth -
                readPixelValue(wrapperStyle.borderInlineStartWidth) -
                readPixelValue(wrapperStyle.borderInlineEndWidth),
        )}px`;
        if (
            viewport.style.getPropertyValue('--datatable-header-scrollbar-width') !==
            headerScrollbarWidth
        ) {
            viewport.style.setProperty(
                '--datatable-header-scrollbar-width',
                headerScrollbarWidth,
            );
        }
        const tableHeader = wrapper.querySelector<HTMLTableSectionElement>(
            ':scope > table > thead',
        );
        const headerScrollbarOffset = `${Math.ceil(
            tableHeader?.getBoundingClientRect().height ?? 0,
        )}px`;
        if (
            viewport.style.getPropertyValue('--datatable-header-scrollbar-offset') !==
            headerScrollbarOffset
        ) {
            viewport.style.setProperty(
                '--datatable-header-scrollbar-offset',
                headerScrollbarOffset,
            );
        }
        const paginationShell = getAdjacentPaginationShell(wrapper);
        const paginationHeight = paginationShell?.getBoundingClientRect().height ?? 52;
        const modalBody = wrapper.closest<HTMLElement>('.modal-body');
        const shell = shellRef.current;
        const shellStyle = shell ? window.getComputedStyle(shell) : null;
        const shellVerticalPadding =
            readPixelValue(shellStyle?.paddingTop) + readPixelValue(shellStyle?.paddingBottom);
        const pageContainer = wrapper.closest('.page-container');
        const pageContainerStyle = pageContainer ? window.getComputedStyle(pageContainer) : null;
        const pageBottomPadding = readPixelValue(pageContainerStyle?.paddingBottom);
        const sidebarFooter = document.querySelector('.sidebar-footer');
        const sidebarFooterRect =
            sidebarFooter instanceof HTMLElement ? sidebarFooter.getBoundingClientRect() : null;
        const sidebarFooterVisible =
            sidebarFooterRect !== null &&
            sidebarFooterRect.width > 0 &&
            sidebarFooterRect.right > 0 &&
            sidebarFooterRect.left < window.innerWidth;
        const sidebarFooterTop = sidebarFooterVisible ? sidebarFooterRect.top : null;
        const isCompactViewport = window.innerWidth <= 640;
        const isNarrowWorkspace = wrapperRect.width <= 420;
        const minimumHeight = isNarrowWorkspace ? 180 : isCompactViewport ? 240 : 320;
        const viewportBottomTarget =
            window.innerHeight - paginationHeight - pageBottomPadding;
        const alignedBottomTarget =
            !isCompactViewport &&
            sidebarFooterTop !== null &&
            sidebarFooterTop > wrapperRect.top + minimumHeight
                ? Math.min(sidebarFooterTop, viewportBottomTarget)
                : viewportBottomTarget;
        const pageAvailableHeight = Math.floor(alignedBottomTarget - wrapperRect.top);
        const modalAvailableHeight = modalBody
            ? Math.floor(
                  modalBody.clientHeight - paginationHeight - shellVerticalPadding,
              )
            : null;
        const modalMinimumHeight = isNarrowWorkspace ? 140 : 180;
        const maxHeight =
            modalAvailableHeight === null
                ? Math.max(minimumHeight, pageAvailableHeight)
                : Math.max(modalMinimumHeight, modalAvailableHeight);

        const nextMaxHeight = `${maxHeight}px`;
        if (wrapper.style.getPropertyValue('--datatable-sticky-max-height') !== nextMaxHeight) {
            wrapper.style.setProperty('--datatable-sticky-max-height', nextMaxHeight);
        }
    }, [stickyHeader]);

    const workspaceFocusMode = useWorkspaceFocusMode({
        targetRef: shellRef,
        enabled: workspaceFocusEnabled,
        onLayoutChange: updateStickyHeaderHeight,
    });
    const focusModeActive = workspaceFocusMode.isActive;
    const focusModeFullscreen = workspaceFocusMode.isFullscreen;

    useEffect(() => {
        updateStickyHeaderHeight();
    });

    useEffect(() => {
        if (!stickyHeader || typeof window === 'undefined') return undefined;

        const wrapper = tableWrapperRef.current;
        if (!wrapper) return undefined;

        const update = () => updateStickyHeaderHeight();
        const main = wrapper.closest('.main-content');
        const modalBody = wrapper.closest('.modal-body');
        const paginationShell = getAdjacentPaginationShell(wrapper);
        const tableHeader = wrapper.querySelector<HTMLTableSectionElement>(
            ':scope > table > thead',
        );
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        const animationFrame = window.requestAnimationFrame(update);

        window.addEventListener('resize', update);
        window.addEventListener('orientationchange', update);
        main?.addEventListener('scroll', update, { passive: true });
        modalBody?.addEventListener('scroll', update, { passive: true });
        resizeObserver?.observe(wrapper);
        if (tableHeader) {
            resizeObserver?.observe(tableHeader);
        }
        if (main instanceof Element) {
            resizeObserver?.observe(main);
        }
        if (modalBody instanceof Element) {
            resizeObserver?.observe(modalBody);
        }
        if (paginationShell) {
            resizeObserver?.observe(paginationShell);
        }

        return () => {
            window.cancelAnimationFrame(animationFrame);
            window.removeEventListener('resize', update);
            window.removeEventListener('orientationchange', update);
            main?.removeEventListener('scroll', update);
            modalBody?.removeEventListener('scroll', update);
            resizeObserver?.disconnect();
            wrapper.style.removeProperty('--datatable-sticky-max-height');
            tableViewportRef.current?.style.removeProperty('--datatable-header-scrollbar-width');
            tableViewportRef.current?.style.removeProperty('--datatable-header-scrollbar-offset');
        };
    }, [stickyHeader, updateStickyHeaderHeight]);
    const currentSelectedIds = useMemo(
        () => selectedRowIds ?? internalSelectedRowIds,
        [internalSelectedRowIds, selectedRowIds],
    );
    const controlledData = useMemo(() => {
        if (!defaultColumnControls) return data;

        const filterableColumns = columns.filter(
            (column) =>
                column.key !== 'actions' &&
                (column.filterable ?? true) &&
                !column.renderFilter &&
                !column.onFilterChange,
        );
        const filtered = data.filter((item) =>
            filterableColumns.every((column) => {
                const selected = internalColumnFilters[getColumnPreferenceId(column)]?.trim();
                if (!selected) return true;
                const query = selected.toLocaleLowerCase(language);
                if (column.filterType === 'select') {
                    const raw = (item as Record<string, unknown>)[String(column.key)];
                    // Options carry stable values while cells may show translated labels.
                    // Computed columns can instead match the option's complete display label.
                    if (raw !== null && raw !== undefined && String(raw) === selected) return true;
                    const label = column.filterOptions?.find(option => option.value === selected)?.label ?? selected;
                    return getDataTableExportValue(column, item).toLocaleLowerCase(language) === label.toLocaleLowerCase(language);
                }
                return getDataTableExportValue(column, item).toLocaleLowerCase(language).includes(query);
            }),
        );

        if (onSort || !internalSortColumn) return filtered;
        const sortDefinition = columns.find(
            (column) =>
                column.key !== 'actions' &&
                (column.sortable ?? true) &&
                String(column.key) === internalSortColumn,
        );
        if (!sortDefinition) return filtered;

        const direction = internalSortDirection === 'desc' ? -1 : 1;
        return [...filtered].sort((left, right) =>
            getDataTableExportValue(sortDefinition, left).localeCompare(
                getDataTableExportValue(sortDefinition, right),
                language,
                { numeric: true, sensitivity: 'base' },
            ) * direction,
        );
    }, [
        columns,
        data,
        defaultColumnControls,
        internalColumnFilters,
        internalSortColumn,
        internalSortDirection,
        language,
        onSort,
    ]);
    const clientInlinePaginationEnabled = pagination?.mode === 'client';
    const clientPagePaginationEnabled = pagination?.mode === 'client-page';
    const clientPaginationEnabled = clientInlinePaginationEnabled || clientPagePaginationEnabled;
    const clientTotalPages = Math.max(1, Math.ceil(controlledData.length / clientPageSize));
    useEffect(() => {
        if (!clientPaginationEnabled) return;
        setClientPage((current) => Math.min(Math.max(1, current), clientTotalPages));
    }, [clientPaginationEnabled, clientTotalPages]);
    const renderedData = useMemo(() => {
        if (!clientPaginationEnabled) return controlledData;
        const start = (clientPage - 1) * clientPageSize;
        return controlledData.slice(start, start + clientPageSize);
    }, [clientPage, clientPageSize, clientPaginationEnabled, controlledData]);
    const visibleRowIds = useMemo(() => renderedData.map((item) => item.id), [renderedData]);
    const selectedIdList = useMemo(() => Array.from(currentSelectedIds), [currentSelectedIds]);
    const selectedRows = useMemo(
        () => controlledData.filter((item) => currentSelectedIds.has(item.id)),
        [controlledData, currentSelectedIds],
    );
    const activeRowActionItem = useMemo(
        () => activeRowActionId === null ? undefined : data.find((item) => item.id === activeRowActionId),
        [activeRowActionId, data],
    );
    const selectionEnabled = !isEditInteraction && selectionColumnActive;
    const selectedCount = selectedIdList.length;
    const allVisibleSelected =
        selectionEnabled &&
        visibleRowIds.length > 0 &&
        visibleRowIds.every((id) => currentSelectedIds.has(id));
    const someVisibleSelected =
        selectionEnabled &&
        visibleRowIds.some((id) => currentSelectedIds.has(id)) &&
        !allVisibleSelected;
    const uploadTemplateConfig = dataOperations?.uploadTemplate;
    const uploadImportColumns = uploadTemplateConfig?.importColumns ?? [];
    const actionColumn = useMemo(
        () => columns.find((column) => column.key === 'actions'),
        [columns],
    );
    const resolvedRowActions = useMemo(() => {
        if (rowActions === false || (isEditInteraction && rowActions == null)) return undefined;
        const config = rowActions || undefined;
        const items = config?.items ?? [];
        if (config?.render) {
            return {
                render: config.render,
                items,
                ariaLabel:
                    config.ariaLabel ||
                    dataTableLabels.rowActionsLabel,
            };
        }
        if (!actionColumn?.render && items.length === 0) return undefined;
        return {
            render: actionColumn?.render,
            items,
            ariaLabel:
                config?.ariaLabel ||
                titleToInputPlaceholder(actionColumn?.title) ||
                dataTableLabels.rowActionsLabel,
        };
    }, [actionColumn, dataTableLabels.rowActionsLabel, isEditInteraction, language, rowActions]);
    const rowActionsEnabled = Boolean(resolvedRowActions);
    const lineActionsKeyboardEnabled = isEditInteraction && rowActions !== undefined && rowActionsEnabled;
    const rowInteractionCandidate = Boolean(
        (!isEditInteraction || rowActions !== undefined) && (
            resolvedRowActions || (
                !isDataTableOutputNotApplicablePolicy(outputExport)
                && columns.some((column) => column.key !== 'actions' && column.output !== false)
            )
        ),
    );
    const [rowActionsTapMode, setRowActionsTapMode] = useState(false);
    const [revealedRowActionId, setRevealedRowActionId] = useState<string | number | null>(null);
    const [rowToolsBlockedByModal, setRowToolsBlockedByModal] = useState(false);

    const clearRowActionsHideTimer = useCallback(() => {
        if (rowActionsHideTimerRef.current !== null) {
            window.clearTimeout(rowActionsHideTimerRef.current);
            rowActionsHideTimerRef.current = null;
        }
    }, []);

    const hideRowActionsOverlay = useCallback(() => {
        clearRowActionsHideTimer();
        activeRowActionElementRef.current = null;
        setActiveRowActionId(null);
        setRowActionsOverlayPosition(null);
        setRowActionSpace(0);
    }, [clearRowActionsHideTimer]);

    useLayoutEffect(() => {
        const updateModalBoundary = () => {
            const blocked = isDataTableBlockedByModal(tableWrapperRef.current);
            setRowToolsBlockedByModal(blocked);
            if (!blocked) return;
            setRowContextMenu(null);
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        };
        updateModalBoundary();
        return subscribeToDataTableModalBoundary(updateModalBoundary);
    }, [hideRowActionsOverlay]);

    const updateRowActionsOverlayPosition = useCallback((rowElement?: HTMLTableRowElement | null) => {
        const targetRow = rowElement ?? activeRowActionElementRef.current;
        const wrapper = tableWrapperRef.current;
        if (!targetRow || !wrapper || typeof window === 'undefined') return;

        const rowRect = targetRow.getBoundingClientRect();
        const rowContentElement = targetRow.querySelector<HTMLElement>(
            ':scope > td:not(.selection-column):not(.actions-col) > :first-child',
        );
        const rowContentRect = rowContentElement?.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const visibleLeft = Math.max(wrapperRect.left, 0);
        const visibleRight = Math.min(wrapperRect.right, window.innerWidth);
        const fallbackRowCenter = rowRect.top + Math.min(rowRect.height / 2, 22);
        const rowCenter = rowContentRect && rowContentRect.height > 0
            ? Math.max(
                rowRect.top,
                Math.min(rowRect.bottom, rowContentRect.top + rowContentRect.height / 2),
            )
            : fallbackRowCenter;

        setRowActionsOverlayPosition({
            top: Math.round(rowCenter),
            right: Math.max(8, Math.round(window.innerWidth - visibleRight + 10)),
            maxWidth: Math.max(120, Math.round(visibleRight - visibleLeft - 20)),
        });
    }, []);

    const showRowActionsOverlay = useCallback(
        (item: T, rowElement: HTMLTableRowElement) => {
            if (!rowActionsEnabled || isDataTableBlockedByModal(rowElement)) return;
            clearRowActionsHideTimer();
            if (activeRowActionElementRef.current !== rowElement) setRowActionSpace(0);
            activeRowActionElementRef.current = rowElement;
            setActiveRowActionId(item.id);
            updateRowActionsOverlayPosition(rowElement);
        },
        [clearRowActionsHideTimer, rowActionsEnabled, updateRowActionsOverlayPosition],
    );

    useLayoutEffect(() => {
        if (lineActionFocusRequest === null || activeRowActionId !== lineActionFocusRequest) return;
        rowActionsOverlayRef.current?.querySelector<HTMLElement>(
            'button:not(:disabled), a[href], [role="button"]:not([aria-disabled="true"])',
        )?.focus();
        setLineActionFocusRequest(null);
    }, [activeRowActionId, lineActionFocusRequest]);

    const resolveRowActionContext = useCallback(
        (_item: T, rowIndex: number) => ({ itemIndex: rowIndex }),
        [],
    );

    const isRowActionHidden = useCallback(
        (action: DataTableRowAction<T>, item: T, rowIndex: number) => {
            const evaluate = action.hidden;
            if (typeof evaluate === 'function') {
                return evaluate(item, rowIndex);
            }
            return Boolean(evaluate);
        },
        [],
    );

    const isRowActionDisabled = useCallback(
        (action: DataTableRowAction<T>, item: T, rowIndex: number) => {
            const evaluate = action.disabled;
            if (typeof evaluate === 'function') {
                return evaluate(item, rowIndex);
            }
            return Boolean(evaluate);
        },
        [],
    );

    const handleRowActionClick = useCallback(
        (action: DataTableRowAction<T>, item: T, rowIndex: number) => {
            if (isDataTableBlockedByModal(tableWrapperRef.current)) return;
            const row = activeRowActionElementRef.current;
            const returnToLine = lineActionsKeyboardEnabled
                && rowActionsOverlayRef.current?.contains(document.activeElement);
            hideRowActionsOverlay();
            if (returnToLine) {
                restoringLineActionFocusRef.current = true;
                row?.focus();
                restoringLineActionFocusRef.current = false;
            }
            void action.onSelect(item, rowIndex);
        },
        [hideRowActionsOverlay, lineActionsKeyboardEnabled],
    );

    const scheduleHideRowActionsOverlay = useCallback(() => {
        if (rowActionsTapMode) return;
        clearRowActionsHideTimer();
        rowActionsHideTimerRef.current = window.setTimeout(() => {
            if (lineActionsKeyboardEnabled && (rowActionsOverlayRef.current?.contains(document.activeElement)
                || activeRowActionElementRef.current === document.activeElement)) return;
            hideRowActionsOverlay();
        }, 140);
    }, [clearRowActionsHideTimer, hideRowActionsOverlay, lineActionsKeyboardEnabled, rowActionsTapMode]);

    const updateSettingsMenuPosition = useCallback(() => {
        const button = settingsButtonRef.current;
        if (!button || typeof window === 'undefined') return;

        const edge = 12;
        const rect = button.getBoundingClientRect();
        const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
        const modalContent = button.closest<HTMLElement>('.modal-content');
        if (modalContent) {
            // Modal.css owns the transformed fixed-position containing block.
            // Keep the popup in the same DOM/focus scope and convert the anchor
            // from viewport coordinates to that block's padding-box coordinates.
            const modalRect = modalContent.getBoundingClientRect();
            const menu = settingsMenuRef.current;
            const contentHeight = menu ? menu.scrollHeight + menu.offsetHeight - menu.clientHeight : 360;
            const nextPosition = resolveDataTableModalSettingsPosition({
                anchor: rect,
                scope: {
                    left: modalRect.left + modalContent.clientLeft,
                    top: modalRect.top + modalContent.clientTop,
                    width: modalContent.clientWidth,
                    height: modalContent.clientHeight,
                },
                viewport: { width: viewportWidth, height: viewportHeight },
                contentHeight,
            });
            setSettingsMenuPosition((current) => current
                && current.top === nextPosition.top && current.right === nextPosition.right
                && current.maxHeight === nextPosition.maxHeight && current.width === nextPosition.width
                ? current : nextPosition);
            return;
        }
        const menuWidth = Math.min(340, Math.max(240, viewportWidth - edge * 2));
        const maxRight = Math.max(edge, viewportWidth - edge - menuWidth);
        const right = Math.max(edge, Math.min(Math.round(viewportWidth - rect.right), maxRight));
        const preferredTop = Math.round(rect.bottom + 8);
        const availableBelow = viewportHeight - preferredTop - edge;
        const availableAbove = rect.top - edge - 8;
        let top = preferredTop;

        if (availableBelow < 180 && availableAbove > availableBelow) {
            const heightAbove = Math.min(520, Math.max(160, availableAbove));
            top = Math.max(edge, Math.round(rect.top - heightAbove - 8));
        }

        const maxHeight = Math.max(160, Math.min(520, Math.round(viewportHeight - top - edge)));
        setSettingsMenuPosition({ top, right, maxHeight });
    }, []);

    const handleSettingsMenuToggle = useCallback(() => {
        setColumnMenuOpen(false);
        setSettingsMenuOpen((current) => {
            if (!current) updateSettingsMenuPosition();
            if (current) setBulkDataTransferOpen(false);
            return !current;
        });
    }, [updateSettingsMenuPosition]);

    useEffect(() => {
        if (selectedRowIds && selectedRowIds.size > 0) {
            setSelectionColumnActive(true);
        }
    }, [selectedRowIds]);

    useEffect(() => {
        if (!rowInteractionCandidate || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            setRowActionsTapMode(false);
            return undefined;
        }
        const media = window.matchMedia('(hover: none), (pointer: coarse)');
        const updateTapMode = () => setRowActionsTapMode(media.matches);
        updateTapMode();
        if (typeof media.addEventListener === 'function') {
            media.addEventListener('change', updateTapMode);
            return () => media.removeEventListener('change', updateTapMode);
        }
        media.addListener(updateTapMode);
        return () => media.removeListener(updateTapMode);
    }, [rowInteractionCandidate]);

    useEffect(() => {
        if (!rowActionsEnabled) {
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
            return;
        }
        if (!rowActionsTapMode) {
            if (revealedRowActionId !== null) {
                hideRowActionsOverlay();
            }
            setRevealedRowActionId(null);
            return;
        }
        if (revealedRowActionId !== null && !data.some((item) => item.id === revealedRowActionId)) {
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        }
    }, [data, hideRowActionsOverlay, revealedRowActionId, rowActionsEnabled, rowActionsTapMode]);

    useEffect(() => {
        if (activeRowActionId !== null && !data.some((item) => item.id === activeRowActionId)) {
            hideRowActionsOverlay();
        }
    }, [activeRowActionId, data, hideRowActionsOverlay]);

    useEffect(() => {
        if (loading) {
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        }
    }, [hideRowActionsOverlay, loading]);

    useEffect(() => {
        if (activeRowActionId === null) return undefined;
        const updatePosition = () => updateRowActionsOverlayPosition();
        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [activeRowActionId, updateRowActionsOverlayPosition]);

    useEffect(() => {
        if (activeRowActionId === null) return undefined;

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return;
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [activeRowActionId, hideRowActionsOverlay]);

    useEffect(() => {
        if (!rowActionsTapMode || activeRowActionId === null) return undefined;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target instanceof Node ? event.target : null;
            if (!target) return;
            if (rowActionsOverlayRef.current?.contains(target)) return;
            if (activeRowActionElementRef.current?.contains(target)) return;

            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        return () => document.removeEventListener('pointerdown', handlePointerDown, true);
    }, [activeRowActionId, hideRowActionsOverlay, rowActionsTapMode]);

    useEffect(() => () => clearRowActionsHideTimer(), [clearRowActionsHideTimer]);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const closeTransientMenusForFloatingLayout = () => {
            setColumnMenuOpen(false);
            setSettingsMenuOpen(false);
            setBulkDataTransferOpen(false);
            setRowContextMenu(null);
            setRevealedRowActionId(null);
            hideRowActionsOverlay();
        };
        window.addEventListener(
            QUBEPRO_FLOATING_LAYOUT_CHANGE_EVENT,
            closeTransientMenusForFloatingLayout,
        );
        return () => window.removeEventListener(
            QUBEPRO_FLOATING_LAYOUT_CHANGE_EVENT,
            closeTransientMenusForFloatingLayout,
        );
    }, [hideRowActionsOverlay]);

    useEffect(() => {
        if (!rowContextMenu) return undefined;

        const close = () => setRowContextMenu(null);
        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target instanceof Node ? event.target : null;
            if (target && rowContextMenuRef.current?.contains(target)) return;
            close();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };

        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown);
        window.addEventListener('resize', close);
        window.addEventListener('scroll', close, true);
        return () => {
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('resize', close);
            window.removeEventListener('scroll', close, true);
        };
    }, [rowContextMenu]);

    useEffect(() => {
        if (!rowContextMenu) return;
        if (loading || !data.some((item) => item.id === rowContextMenu.itemId)) {
            setRowContextMenu(null);
        }
    }, [data, loading, rowContextMenu]);

    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate = someVisibleSelected;
        }
    }, [someVisibleSelected]);

    const handleSelectionModeToggle = useCallback(() => {
        setSelectionColumnActive((current) => {
            const next = !current;
            if (!next) {
                setInternalSelectedRowIds(new Set());
                if (visibleRowIds.length > 0) {
                    onToggleAllRowSelection?.(visibleRowIds, false);
                }
            }
            return next;
        });
    }, [onToggleAllRowSelection, visibleRowIds]);

    const handleToggleRowSelection = useCallback(
        (id: string | number, checked: boolean) => {
            if (!selectedRowIds) {
                setInternalSelectedRowIds((current) => {
                    const next = new Set(current);
                    if (checked) next.add(id);
                    else next.delete(id);
                    return next;
                });
            }
            onToggleRowSelection?.(id, checked);
        },
        [onToggleRowSelection, selectedRowIds],
    );

    const handleToggleAllRowSelection = useCallback(
        (ids: Array<string | number>, checked: boolean) => {
            if (!selectedRowIds) {
                setInternalSelectedRowIds((current) => {
                    const next = new Set(current);
                    ids.forEach((id) => {
                        if (checked) next.add(id);
                        else next.delete(id);
                    });
                    return next;
                });
            }
            onToggleAllRowSelection?.(ids, checked);
        },
        [onToggleAllRowSelection, selectedRowIds],
    );

    const clearSelection = useCallback(() => {
        setInternalSelectedRowIds(new Set());
        if (selectedIdList.length > 0) {
            onToggleAllRowSelection?.(selectedIdList, false);
        }
    }, [onToggleAllRowSelection, selectedIdList]);

    const bulkActionContext = useMemo<DataTableBulkActionContext<T>>(
        () => ({
            selectedIds: selectedIdList,
            selectedRows,
            visibleRowIds,
            clearSelection,
        }),
        [clearSelection, selectedIdList, selectedRows, visibleRowIds],
    );

    const resolvedBulkActions = useMemo(
        () => (bulkActions ?? []).filter((action) => {
            const hidden = typeof action.hidden === 'function'
                ? action.hidden(bulkActionContext)
                : Boolean(action.hidden);
            return !hidden;
        }),
        [bulkActionContext, bulkActions],
    );

    useEffect(() => {
        if (!columnMenuOpen && !settingsMenuOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (toolbarRef.current && !toolbarRef.current.contains(e.target as Node)) {
                setColumnMenuOpen(false);
                setSettingsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc as unknown as EventListener);
        return () => document.removeEventListener('mousedown', onDoc as unknown as EventListener);
    }, [columnMenuOpen, settingsMenuOpen]);

    useEffect(() => {
        if (!settingsMenuOpen) return undefined;
        updateSettingsMenuPosition();
        let frame = 0;
        const schedulePositionUpdate = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(updateSettingsMenuPosition);
        };
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedulePositionUpdate);
        const modalContent = settingsButtonRef.current?.closest('.modal-content');
        if (settingsMenuRef.current && modalContent) {
            observer?.observe(settingsMenuRef.current);
            observer?.observe(modalContent);
        }
        window.addEventListener('resize', updateSettingsMenuPosition);
        window.addEventListener('scroll', updateSettingsMenuPosition, true);
        return () => {
            cancelAnimationFrame(frame);
            observer?.disconnect();
            window.removeEventListener('resize', updateSettingsMenuPosition);
            window.removeEventListener('scroll', updateSettingsMenuPosition, true);
        };
    }, [bulkDataTransferOpen, columnMenuOpen, settingsMenuOpen, updateSettingsMenuPosition]);

    useEffect(() => {
        if (!settingsMenuOpen) return undefined;
        const handleSettingsEscape = (event: KeyboardEvent) => {
            if (event.key !== 'Escape' || event.defaultPrevented || isDataTableBlockedByModal(shellRef.current)) return;
            const visibleMenus = Array.from(document.querySelectorAll('.datatable-settings-menu'))
                .filter((menu) => menu.getClientRects().length > 0 && !isDataTableBlockedByModal(menu));
            if (visibleMenus[visibleMenus.length - 1] !== settingsMenuRef.current) return;

            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            const target = event.target instanceof Element ? event.target : null;
            const inColumns = Boolean(target?.closest('.datatable-settings-columns'));
            // Collapse the focused inner section first; otherwise the last DOM
            // section is the uppermost open sub-surface in the settings menu.
            if (bulkDataTransferOpen && (!columnMenuOpen || !inColumns)) {
                setBulkDataTransferOpen(false);
                bulkTransferButtonRef.current?.focus({ preventScroll: true });
            } else if (columnMenuOpen) {
                setColumnMenuOpen(false);
                columnSettingsButtonRef.current?.focus({ preventScroll: true });
            } else {
                setSettingsMenuOpen(false);
                settingsButtonRef.current?.focus({ preventScroll: true });
            }
        };
        document.addEventListener('keydown', handleSettingsEscape, true);
        return () => document.removeEventListener('keydown', handleSettingsEscape, true);
    }, [bulkDataTransferOpen, columnMenuOpen, settingsMenuOpen]);

    const resetImportState = useCallback(() => {
        setImportPayload(null);
        setImportParseResult(null);
        setImportError(null);
        setImportMessage(null);
        setImportParsing(false);
        setImportSubmitting(false);
    }, []);

    const openImportModal = useCallback(() => {
        resetImportState();
        setImportModalOpen(true);
    }, [resetImportState]);

    const closeImportModal = useCallback(() => {
        if (importParsing || importSubmitting) return;
        setImportModalOpen(false);
        resetImportState();
    }, [importParsing, importSubmitting, resetImportState]);

    const handleImportFileSelect = useCallback(
        async (file: File | null) => {
            setImportPayload(null);
            setImportParseResult(null);
            setImportError(null);
            setImportMessage(null);
            if (!file) return;
            if (!uploadImportColumns.length) {
                setImportError(dataTableLabels.operationNotReady);
                return;
            }

            try {
                setImportParsing(true);
                const payload = await parseDataTableImportFile(
                    file,
                    uploadImportColumns,
                    {
                        ...uploadTemplateConfig?.importOptions,
                        messages: {
                            unknownHeader: (header) =>
                                dataTableLabels.importUnknownHeader.replace('{header}', header),
                            missingRequiredHeader: (header) =>
                                dataTableLabels.importMissingRequiredHeader.replace('{header}', header),
                            requiredCell: (header) =>
                                dataTableLabels.importRequiredCell.replace('{header}', header),
                            maxRowsExceeded: (maxRows) =>
                                dataTableLabels.importMaxRowsExceeded.replaceAll(
                                    '{maxRows}',
                                    String(maxRows),
                                ),
                            ...uploadTemplateConfig?.importOptions?.messages,
                        },
                    },
                );
                setImportPayload(payload);
                setImportParseResult(payload);
            } catch (err) {
                setImportError(err instanceof Error ? err.message : dataTableLabels.importFailed);
            } finally {
                setImportParsing(false);
            }
        },
        [dataTableLabels, uploadImportColumns, uploadTemplateConfig?.importOptions],
    );

    const handleImportSubmit = useCallback(async () => {
        if (!importPayload || !uploadTemplateConfig?.onImport || importSubmitting) return;
        if (importPayload.errors.length > 0 || importPayload.validRows.length === 0) {
            setImportError(dataTableLabels.importNoValidRows);
            return;
        }

        try {
            setImportSubmitting(true);
            setImportError(null);
            setImportMessage(null);
            const response = await uploadTemplateConfig.onImport(importPayload);
            const result = response && typeof response === 'object' ? response : undefined;
            if (result?.success === false) {
                const detail = result.errors?.filter(Boolean).join('\n');
                setImportError([result.message || dataTableLabels.importFailed, detail].filter(Boolean).join('\n'));
                return;
            }
            setImportMessage(result?.message || dataTableLabels.importSucceeded);
            if (result?.closeModal !== false) {
                setImportModalOpen(false);
                resetImportState();
            }
        } catch (err) {
            setImportError(err instanceof Error ? err.message : dataTableLabels.importFailed);
        } finally {
            setImportSubmitting(false);
        }
    }, [dataTableLabels.importFailed, dataTableLabels.importNoValidRows, dataTableLabels.importSucceeded, importPayload, importSubmitting, resetImportState, uploadTemplateConfig]);

    const hiddenIds = useMemo(
        () => computeHiddenIds(columns, effectivePreferencesKey ? prefs.tablePrefs : undefined),
        [columns, effectivePreferencesKey, prefs.tablePrefs],
    );

    const normalizedColumns = useMemo(() => {
        const savedOrder = effectivePreferencesKey ? prefs.tablePrefs?.columnOrder : undefined;
        const visible = columns.filter((c) => c.key === 'actions' || !hiddenIds.has(getColumnPreferenceId(c)));
        const dataOrdered = orderVisibleDataColumns(columns, hiddenIds, savedOrder);
        const actionsVisible = rowActionsEnabled ? [] : visible.filter((c) => c.key === 'actions');
        const pinned = dataOrdered.find(column => column.pinned === 'start');
        const ordered = [...(pinned ? [pinned] : []), ...dataOrdered.filter(column => column !== pinned), ...actionsVisible];
        if (!defaultColumnControls) return ordered;
        return ordered.map((column) =>
            column.key === 'actions'
                ? column
                : {
                    ...column,
                    sortable: column.sortable ?? true,
                    filterable: column.filterable ?? true,
                },
        );
    }, [
        columns,
        defaultColumnControls,
        effectivePreferencesKey,
        hiddenIds,
        prefs.tablePrefs?.columnOrder,
        rowActionsEnabled,
    ]);

    const columnSettingsItems = useMemo(() => {
        const items = columns.filter((c) => c.key !== 'actions');
        return {
            standard: items.filter((c) => c.settingsGroup !== 'customFields'),
            customFields: items.filter((c) => c.settingsGroup === 'customFields'),
        };
    }, [columns]);

    const outputSourceColumns = useMemo(
        () => normalizedColumns.filter((column) => (
            column.key !== 'actions'
            && column.output !== false
            && !isSensitiveDataTableExportColumn(column)
        )),
        [normalizedColumns],
    );
    const reportScope: DataTableOutputScope = selectedRows.length > 0 ? 'SELECTED_ROWS' : 'VISIBLE_ROWS';
    const automaticFilters = useMemo(() => {
        const items: Array<{ label: string; value: string }> = [];
        for (const column of normalizedColumns) {
            const value = column.filterValue?.trim();
            if (value) items.push({ label: getDataTableExportHeader(column), value });
        }
        for (const rule of smartFilter?.value?.rules || []) {
            const field = smartFilter?.fields.find((item) => item.key === rule.field);
            const value = [rule.value, rule.valueTo].filter(Boolean).join(' - ');
            if (value) items.push({ label: field?.label || rule.field, value });
        }
        return items;
    }, [normalizedColumns, smartFilter?.fields, smartFilter?.value?.rules]);
    const automaticQuickSearch = smartFilter?.value?.quickSearch?.trim() || undefined;
    const automaticSort = useMemo(() => {
        const activeSortColumn = sortColumn ?? internalSortColumn;
        if (!activeSortColumn) return undefined;
        const column = normalizedColumns.find((item) => String(item.key) === String(activeSortColumn));
        const label = column ? getDataTableExportHeader(column) : String(activeSortColumn);
        const activeSortDirection = sortColumn ? sortDirection : internalSortDirection;
        return { label, direction: activeSortDirection === 'desc' ? 'desc' as const : 'asc' as const };
    }, [internalSortColumn, internalSortDirection, normalizedColumns, sortColumn, sortDirection]);
    const outputNotApplicable = isDataTableOutputNotApplicablePolicy(outputExport);
    const outputExportConfig: DataTableOutputExportConfig | undefined = outputNotApplicable
        ? undefined
        : outputExport;
    const baseOutputRequest = useMemo<DataTableOutputExportRequest>(() => ({
        schemaVersion: 2,
        timeZone: resolveBrowserTimeZone(),
        dateFormat: outputDateTime.dateFormat,
        timeFormat: outputDateTime.timeFormat,
        title: outputExportConfig?.title,
        fileName: outputExportConfig?.fileName,
        tableId: effectivePreferencesKey,
        columns: [],
        rows: [],
        scope: reportScope,
        filters: automaticFilters,
        quickSearch: automaticQuickSearch,
        filterSummary: outputExportConfig?.filterSummary?.trim() || undefined,
        sort: automaticSort,
    }), [
        automaticFilters,
        automaticQuickSearch,
        automaticSort,
        effectivePreferencesKey,
        outputExportConfig?.fileName,
        outputExportConfig?.filterSummary,
        outputExportConfig?.title,
        outputDateTime.dateFormat,
        outputDateTime.timeFormat,
        reportScope,
    ]);
    const prepareOutputRequest = useCallback((scope: DataTableOutputScope): DataTableOutputExportRequest => {
        if (scope === 'ALL_FILTERED_ROWS') throw new Error('ALL_FILTERED_ROWS requires the server provider.');
        const dataset = buildSmartDataTableOutputDataset(
            outputSourceColumns,
            scope === 'SELECTED_ROWS' ? selectedRows : renderedData,
        );
        return { ...baseOutputRequest, ...dataset, scope };
    }, [baseOutputRequest, outputSourceColumns, renderedData, selectedRows]);
    const buildRowOutputRequest = useCallback((item: T): DataTableOutputExportRequest => {
        const dataset = buildSmartDataTableOutputDataset(outputSourceColumns, [item]);
        return {
            ...baseOutputRequest,
            columns: dataset.columns,
            rows: dataset.rows,
            scope: 'SELECTED_ROWS',
        };
    }, [baseOutputRequest, outputSourceColumns]);
    const dataTableOutputExport = useDataTableOutputExport({
        enabled: !outputNotApplicable && outputSourceColumns.length > 0,
        language,
        companyScopeId: outputExportConfig?.companyScopeId,
        allFilteredTemplatePolicy: outputExportConfig?.allFilteredTemplatePolicy,
        defaultScope: outputExportConfig?.defaultScope,
        ...baseOutputRequest,
        prepareRequest: prepareOutputRequest,
        hasSelectedRows: selectedRows.length > 0,
        columnCount: outputSourceColumns.length,
        queueAllFilteredOutput: outputExportConfig?.defaultScope === 'ALL_FILTERED_ROWS'
            ? ({ format, locale, templateId, retry }) => outputExportConfig.queueAllFilteredOutput({
                tableId: effectivePreferencesKey,
                locale,
                format,
                templateId,
                timeZone: baseOutputRequest.timeZone,
                dateFormat: baseOutputRequest.dateFormat,
                timeFormat: baseOutputRequest.timeFormat,
                title: baseOutputRequest.title,
                fileName: baseOutputRequest.fileName,
                filters: automaticFilters,
                quickSearch: automaticQuickSearch,
                sort: automaticSort,
                retry,
            })
            : undefined,
        canSetCompanyDefault: hasPermission('output-template:set-default'),
        canCloneTemplate: hasPermission('output-template:create')
            && hasPermission('output-template:update')
            && hasPermission('output-template:publish'),
    });
    const canQueueAllFilteredRows = selectedRows.length === 0
        && outputExportConfig?.defaultScope === 'ALL_FILTERED_ROWS';
    const hasOutputRows = selectedRows.length > 0 || renderedData.length > 0 || canQueueAllFilteredRows;
    const resolvedDataOperations = useMemo<ResolvedDataTableOperation[]>(() => {
        const manifestByKey = indexManifestOperations(manifestTableOperations?.operations);
        return DATA_TABLE_OPERATION_ORDER.flatMap((key) => {
            const corporateOutput = key === 'corporateOutput';
            const explicitCorporateLocal = dataOperations?.corporateOutput;
            const explicitCorporateManifest = manifestByKey.get('corporateOutput');
            const local = corporateOutput
                ? explicitCorporateLocal ?? dataOperations?.exportPdf ?? dataOperations?.exportExcel ?? dataOperations?.exportCsv
                : dataOperations?.[key];
            const manifest = corporateOutput
                ? explicitCorporateManifest ?? manifestByKey.get('exportPdf') ?? manifestByKey.get('exportExcel') ?? manifestByKey.get('exportCsv')
                : manifestByKey.get(key);
            if (local?.visible === false) return [];
            const outputFormat = key === 'exportExcel'
                ? 'XLSX' as const
                : key === 'exportCsv'
                    ? 'CSV' as const
                    : key === 'exportPdf'
                        ? 'PDF' as const
                        : null;
            const standardOutputCandidate = Boolean(
                (corporateOutput || outputFormat)
                && !outputNotApplicable
                && outputSourceColumns.length > 0,
            );
            const autoExport = Boolean(
                standardOutputCandidate
                && (corporateOutput || (outputFormat && dataTableOutputExport.isFormatReady(outputFormat))),
            );

            if (standardOutputCandidate && !autoExport && !local?.onClick) return [];

            const isStandardUpload =
                key === 'uploadTemplate' &&
                Boolean(local?.onImport) &&
                Boolean(local?.importColumns?.length);
            const hasDefinition = Boolean(local || manifest || autoExport);
            if (!hasDefinition) return [];

            const supported = corporateOutput
                ? explicitCorporateLocal?.supported ?? explicitCorporateManifest?.supported ?? autoExport
                : local?.supported ?? manifest?.supported ?? (autoExport || Boolean(local?.onClick || isStandardUpload));
            const permission = corporateOutput
                ? explicitCorporateLocal?.permission ?? explicitCorporateManifest?.permissionCode ?? 'output-job:create'
                : local?.permission ?? manifest?.permissionCode;
            const allowed = !permission || hasPermission(permission);
            const handler = corporateOutput
                ? (hasOutputRows && !loading ? dataTableOutputExport.openCorporateOutput : undefined)
                : local?.onClick ?? (
                isStandardUpload
                    ? openImportModal
                : autoExport && outputFormat && hasOutputRows && !loading
                        ? () => { void dataTableOutputExport.startExport(outputFormat); }
                            : undefined
                );
            const fallbackLabel = getOperationFallbackLabel(key, dataTableLabels);
            const label = corporateOutput
                ? explicitCorporateLocal?.label
                    ?? getLocalizedTableText(explicitCorporateManifest?.name, language, fallbackLabel)
                    ?? fallbackLabel
                : local?.label
                    ?? getLocalizedTableText(manifest?.name, language, fallbackLabel)
                    ?? fallbackLabel;

            let disabledReason: string | undefined;
            if (autoExport && !hasOutputRows) {
                disabledReason = dataTableLabels.operationNoRows;
            } else if (!supported) {
                disabledReason =
                    local?.disabledReason ??
                    getLocalizedTableText(manifest?.disabledReason, language, dataTableLabels.operationUnsupported) ??
                    dataTableLabels.operationUnsupported;
            } else if (!allowed) {
                disabledReason = dataTableLabels.operationNoPermission;
            } else if (!handler) {
                disabledReason =
                    local?.disabledReason ??
                    getLocalizedTableText(manifest?.disabledReason, language, dataTableLabels.operationNotReady) ??
                    dataTableLabels.operationNotReady;
            }

            const description =
                disabledReason ??
                (corporateOutput ? explicitCorporateLocal?.description : local?.description) ??
                getLocalizedTableText(corporateOutput ? explicitCorporateManifest?.description : manifest?.description, language) ??
                (corporateOutput ? dataTableLabels.operationCorporateOutputDescription : undefined);

            return [
                {
                    key,
                    label,
                    description,
                    icon: DATA_TABLE_OPERATION_ICONS[key],
                    disabled: Boolean(disabledReason),
                    onClick: handler,
                },
            ];
        });
    }, [dataOperations, dataTableLabels, dataTableOutputExport.isFormatReady, dataTableOutputExport.openCorporateOutput, dataTableOutputExport.startExport, hasOutputRows, hasPermission, language, loading, manifestTableOperations?.operations, openImportModal, outputNotApplicable, outputSourceColumns.length]);
    const corporateOutputOperation = resolvedDataOperations.find(
        (operation) => operation.key === 'corporateOutput',
    );
    const legacyOutputOperationKeys = useMemo(
        () => new Set<DataTableOperationKey>(['exportExcel', 'exportCsv', 'exportPdf']),
        [],
    );
    const bulkDataTransferOperations = resolvedDataOperations.filter((operation) => {
        if (operation.key === 'corporateOutput') return false;
        if (corporateOutputOperation && legacyOutputOperationKeys.has(operation.key)) return false;
        return true;
    });
    const contextRowItem = rowContextMenu
        ? controlledData.find((item) => item.id === rowContextMenu.itemId)
        : undefined;
    const contextRowIndex = contextRowItem
        ? controlledData.findIndex((item) => item.id === contextRowItem.id)
        : -1;
    const contextRowActions = contextRowItem && contextRowIndex >= 0
        ? (resolvedRowActions?.items ?? []).filter(
            (action) => !isRowActionHidden(action, contextRowItem, contextRowIndex),
        )
        : [];
    const rowContextMenuEnabled = Boolean(
        (!isEditInteraction || rowActions !== undefined)
        && (
            corporateOutputOperation
            || resolvedRowActions?.render
            || (resolvedRowActions?.items?.length ?? 0) > 0
        ),
    );

    const openRowContextMenu = useCallback((
        item: T,
        clientX: number,
        clientY: number,
    ) => {
        if (isDataTableBlockedByModal(tableWrapperRef.current)) return;
        hideRowActionsOverlay();
        setRevealedRowActionId(null);
        setRowContextMenu({ itemId: item.id, clientX, clientY });
    }, [hideRowActionsOverlay]);

    const openRowContextMenuFromRow = useCallback((item: T, rowElement: HTMLTableRowElement) => {
        const rect = rowElement.getBoundingClientRect();
        openRowContextMenu(
            item,
            Math.max(12, Math.min(rect.right - 12, window.innerWidth - 12)),
            Math.max(12, Math.min(rect.top + Math.min(rect.height, 44), window.innerHeight - 12)),
        );
    }, [openRowContextMenu]);

    const handleSortClick = (column: Column<T>) => {
        if (!column.sortable || column.key === 'actions') return;
        if (onSort) {
            onSort(column.key as keyof T);
            return;
        }
        if (!defaultColumnControls) return;
        const columnKey = String(column.key);
        setInternalSortColumn(columnKey);
        setInternalSortDirection((direction) => internalSortColumn === columnKey
            ? (direction === 'asc' ? 'desc' : 'asc')
            : 'asc');
    };

    const handleRefresh = useCallback(() => {
        if (!onRefresh || loading) return;
        void onRefresh();
    }, [loading, onRefresh]);

    const handleDataRowClick = useCallback(
        (event: MouseEvent<HTMLTableRowElement>, item: T) => {
            if (isDataTableBlockedByModal(event.currentTarget)) return;
            if (shouldIgnoreRowClick(event.target)) return;
            if (rowActionsTapMode && rowContextMenuEnabled) {
                openRowContextMenuFromRow(item, event.currentTarget);
                return;
            }
            if (rowActionsEnabled && rowActionsTapMode) {
                const rowElement = event.currentTarget;
                setRevealedRowActionId((current) => {
                    if (current === item.id) {
                        hideRowActionsOverlay();
                        return null;
                    }
                    showRowActionsOverlay(item, rowElement);
                    return item.id;
                });
                return;
            }
            onRowClick?.(item);
        },
        [hideRowActionsOverlay, onRowClick, openRowContextMenuFromRow, rowActionsEnabled, rowActionsTapMode, rowContextMenuEnabled, showRowActionsOverlay],
    );

    const toggleColumnVisibility = useCallback(
        (colId: string, nextVisible: boolean) => {
            if (!effectivePreferencesKey) return;
            const dataCols = columns.filter((c) => c.key !== 'actions');
            const allIds = dataCols.map(getColumnPreferenceId);
            const nextHidden = new Set(hiddenIds);
            if (nextVisible) {
                nextHidden.delete(colId);
            } else {
                nextHidden.add(colId);
            }
            const hiddenArr = allIds.filter((id) => nextHidden.has(id));
            prefs.setHiddenColumns(hiddenArr);
            if (nextVisible && dataCols.some((column) => getColumnPreferenceId(column) === colId && column.defaultHidden)) {
                prefs.setColumnOrder(mergeFullDataColumnOrder(columns, prefs.tablePrefs?.columnOrder));
            }
        },
        [columns, effectivePreferencesKey, hiddenIds, prefs.setColumnOrder, prefs.setHiddenColumns, prefs.tablePrefs?.columnOrder],
    );

    const startResize = useCallback(
        (e: React.MouseEvent, colId: string) => {
            if (!effectivePreferencesKey) return;
            e.preventDefault();
            e.stopPropagation();
            const th = (e.currentTarget as HTMLElement).closest('th');
            if (!th) return;
            const startX = e.clientX;
            const startW = th.getBoundingClientRect().width;
            const column = columns.find(item => getColumnPreferenceId(item) === colId);
            const minimum = resolveDataTableColumnWidth(column ?? {}).minWidth;
            const direction = window.getComputedStyle(th).direction === 'rtl' ? -1 : 1;
            const nextWidth = (x: number) => Math.max(minimum, Math.round(startW + (x - startX) * direction));
            const onMove = (ev: Event) => {
                const me = ev as globalThis.MouseEvent;
                const w = nextWidth(me.clientX);
                th.style.width = `${w}px`;
            };
            const onUp = (ev: Event) => {
                const me = ev as globalThis.MouseEvent;
                const w = nextWidth(me.clientX);
                prefs.patchColumnWidths({ [colId]: w });
                th.style.width = '';
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
        },
        [columns, effectivePreferencesKey, prefs.patchColumnWidths],
    );

    const handleColumnDragStart = useCallback((e: React.DragEvent, colId: string) => {
        e.stopPropagation();
        e.dataTransfer.setData('text/plain', colId);
        e.dataTransfer.effectAllowed = 'move';
    }, []);

    const handleColumnDragEnd = useCallback(() => {
        setDragOverColId(null);
    }, []);

    const handleColumnDrop = useCallback(
        (e: React.DragEvent, dropId: string) => {
            e.preventDefault();
            e.stopPropagation();
            setDragOverColId(null);
            if (!effectivePreferencesKey) return;
            const dragId = e.dataTransfer.getData('text/plain').trim();
            if (!dragId || dragId === dropId) return;
            const full = mergeFullDataColumnOrder(columns, prefs.tablePrefs?.columnOrder);
            const next = reorderColumnIdsInFullList(full, dragId, dropId);
            prefs.setColumnOrder(next);
        },
        [effectivePreferencesKey, columns, prefs.tablePrefs?.columnOrder, prefs.setColumnOrder],
    );

    const columnSizing = (column: Column<T>) => {
        const pid = getColumnPreferenceId(column);
        const fromPref =
            column.key !== 'actions' &&
            effectivePreferencesKey &&
            prefs.tablePrefs?.columnWidths &&
            prefs.tablePrefs.columnWidths[pid] != null
                ? prefs.tablePrefs.columnWidths[pid]
                : undefined;
        return resolveDataTableColumnWidth(column, fromPref);
    };
    const resolvedWidth = (column: Column<T>): CSSProperties => {
        const { width, minWidth } = columnSizing(column);
        return { width, minWidth };
    };
    const minimumTableWidth = dataTableMinimumWidth(normalizedColumns.map(columnSizing), selectionEnabled);

    const modeClass = `datatable-row-${effectiveRowMode}`;
    const importPreviewRows = importParseResult?.rows.slice(0, 5) ?? [];
    const importPreviewHeaders = importParseResult?.headers.slice(0, 8) ?? [];
    const importCanSubmit = Boolean(
        importPayload &&
            uploadTemplateConfig?.onImport &&
            importPayload.errors.length === 0 &&
            importPayload.validRows.length > 0 &&
            !importParsing &&
            !importSubmitting,
    );
    const importFormFields: FormField[] = [{
        name: 'importFile',
        label: dataTableLabels.importChooseFile,
        description: dataTableLabels.importChooseFileHint,
        type: 'file',
        accept: '.csv,text/csv',
        disabled: importParsing || importSubmitting,
        colSpan: 12,
    }];
    const settingsMenuStyle = settingsMenuPosition ? {
        '--datatable-settings-menu-top': `${settingsMenuPosition.top}px`,
        '--datatable-settings-menu-right': `${settingsMenuPosition.right}px`,
        '--datatable-settings-menu-max-height': `${settingsMenuPosition.maxHeight}px`,
        ...(settingsMenuPosition.width === undefined ? {} : { '--datatable-settings-menu-width': `${settingsMenuPosition.width}px` }),
    } as CSSProperties : undefined;
    const bulkActionsNode = selectionEnabled && selectedCount > 0 ? (
        <div
            className="datatable-bulk-actions datatable-bulk-actions-dock"
            role="toolbar"
            aria-label={dataTableLabels.bulkActionsLabel}
        >
            <span className="datatable-bulk-count">
                {dataTableLabels.bulkSelectedLabel.replace('{count}', String(selectedCount))}
            </span>
            {resolvedBulkActions.map((action) => {
                const disabled = typeof action.disabled === 'function'
                    ? action.disabled(bulkActionContext)
                    : Boolean(action.disabled);
                const variant = action.variant ?? 'secondary';
                return (
                    <button
                        key={action.key}
                        type="button"
                        className={`datatable-bulk-action-btn is-${variant}`}
                        onClick={() => void action.onClick(bulkActionContext)}
                        disabled={disabled}
                        title={action.title || action.label}
                    >
                        {action.icon && <Icon name={action.icon} size={16} />}
                        <span>{action.label}</span>
                    </button>
                );
            })}
        </div>
    ) : null;
    const rowActionsOverlayPortalTarget =
        typeof document !== 'undefined' ? document.body : null;
    const activeRowActionContext = useMemo<DataTableRowActionContext | undefined>(
        () =>
            activeRowActionItem
                ? resolveRowActionContext(activeRowActionItem, controlledData.findIndex((item) => item.id === activeRowActionItem.id))
                : undefined,
        [activeRowActionItem, controlledData, resolveRowActionContext],
    );
    const rowActionsOverlayNode =
        !rowToolsBlockedByModal && resolvedRowActions && activeRowActionItem && rowActionsOverlayPosition ? (
            <TableRowActionsSurface
                ref={rowActionsOverlayRef}
                protectedRow={activeRowActionElementRef.current}
                reservedSpace={rowActionSpace}
                onSpaceRequirementChange={setRowActionSpace}
                className="datatable-row-actions-overlay"
                data-no-row-click="true"
                role="toolbar"
                aria-label={resolvedRowActions.ariaLabel}
                style={{
                    top: `${rowActionsOverlayPosition.top}px`,
                    right: `${rowActionsOverlayPosition.right}px`,
                    maxWidth: `${rowActionsOverlayPosition.maxWidth}px`,
                }}
                onMouseEnter={clearRowActionsHideTimer}
                onMouseLeave={scheduleHideRowActionsOverlay}
                onBlur={lineActionsKeyboardEnabled ? (event) => {
                    const target = event.relatedTarget;
                    if (target instanceof Node && (event.currentTarget.contains(target)
                        || activeRowActionElementRef.current?.contains(target))) return;
                    hideRowActionsOverlay();
                } : undefined}
                onKeyDown={lineActionsKeyboardEnabled ? (event) => {
                    if (event.key !== 'Escape' && event.key !== 'Tab') return;
                    event.preventDefault();
                    event.stopPropagation();
                    const row = activeRowActionElementRef.current;
                    const selector = 'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]';
                    const tabbable = (element: HTMLElement) => element.tabIndex >= 0
                        && !element.closest('[hidden], [inert], [aria-hidden="true"], [aria-disabled="true"]')
                        && element.getClientRects().length > 0;
                    if (event.key === 'Tab') {
                        // The action portal is outside the modal's native Tab sequence.
                        const actions = Array.from(event.currentTarget.querySelectorAll<HTMLElement>(selector)).filter(tabbable);
                        const index = actions.findIndex(action => action === document.activeElement
                            || action.contains(document.activeElement));
                        const next = actions[index + (event.shiftKey ? -1 : 1)];
                        if (next) {
                            next.focus();
                            return;
                        }
                        if (!event.shiftKey && row) {
                            const scope = row.closest('[role="dialog"]') ?? row.ownerDocument;
                            const fields = Array.from(scope.querySelectorAll<HTMLElement>(selector)).filter(tabbable);
                            const afterRow = fields[fields.indexOf(row) + 1] ?? fields[0];
                            hideRowActionsOverlay();
                            afterRow?.focus();
                            return;
                        }
                    }
                    restoringLineActionFocusRef.current = true;
                    hideRowActionsOverlay();
                    row?.focus();
                    restoringLineActionFocusRef.current = false;
                } : undefined}
                onClickCapture={(event) => {
                    // Legacy render/CrudActionButtons stop bubbling. Let the
                    // target handler run before removing its button. React may
                    // flush between native capture and bubble listeners, so this
                    // must run after the event dispatch, not in a microtask.
                    const control = event.target instanceof Element ? event.target.closest('button, a, [role="button"]') : null;
                    if (resolvedRowActions.render && control && !control.matches(':disabled, [aria-disabled="true"]')) {
                        clearRowActionsHideTimer();
                        rowActionsHideTimerRef.current = window.setTimeout(hideRowActionsOverlay, 0);
                    }
                }}
            >
                {resolvedRowActions.render
                    ? resolvedRowActions.render(activeRowActionItem)
                    : (
                        <div className="action-buttons">
                            {resolvedRowActions.items && activeRowActionContext && activeRowActionContext.itemIndex >= 0
                                ? resolvedRowActions.items
                                    .filter((action) => !isRowActionHidden(action, activeRowActionItem, activeRowActionContext.itemIndex))
                                    .map((action) => {
                                        const rowIndex = activeRowActionContext.itemIndex;
                                        const allowedByPermission = action.permission ? hasPermission(action.permission) : true;
                                        const disabled = !allowedByPermission || isRowActionDisabled(action, activeRowActionItem, rowIndex);
                                        const title = action.title || action.label;
                                        const ariaLabel = action.ariaLabel || title;
                                        return (
                                            <button
                                                key={action.key}
                                                type="button"
                                                className={action.buttonClass || 'btn-view'}
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    event.stopPropagation();
                                                    if (disabled) return;
                                                    void handleRowActionClick(action, activeRowActionItem, rowIndex);
                                                }}
                                                title={disabled && action.disabledReason ? action.disabledReason : title}
                                                aria-label={ariaLabel}
                                                disabled={disabled}
                                            >
                                                {action.icon ? <Icon name={action.icon} size={16} /> : null}
                                            </button>
                                        );
                                    })
                                : null}
                        </div>
                    )}
                {rowContextMenuEnabled ? (
                    <button
                        type="button"
                        className="datatable-row-more-actions"
                        title={dataTableLabels.rowMoreActionsLabel}
                        aria-label={dataTableLabels.rowMoreActionsLabel}
                        onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            const rect = event.currentTarget.getBoundingClientRect();
                            openRowContextMenu(activeRowActionItem, rect.right, rect.bottom + 6);
                        }}
                    >
                        <Icon name="menu" size={16} />
                    </button>
                ) : null}
            </TableRowActionsSurface>
        ) : null;
    const rowContextMenuNode = !rowToolsBlockedByModal && rowContextMenu && contextRowItem && contextRowIndex >= 0 ? (
        <div
            ref={rowContextMenuRef}
            className="datatable-row-context-menu"
            role="menu"
            aria-label={dataTableLabels.rowContextMenuLabel}
            style={resolveDataTableRowContextMenuPosition(
                rowContextMenu.clientX,
                rowContextMenu.clientY,
            )}
            onContextMenu={(event) => event.preventDefault()}
        >
            {corporateOutputOperation ? (
                <button
                    type="button"
                    className="datatable-row-context-menu-item is-primary"
                    role="menuitem"
                    disabled={corporateOutputOperation.disabled}
                    title={corporateOutputOperation.description || dataTableLabels.rowCorporateOutputDescription}
                    onClick={() => {
                        if (corporateOutputOperation.disabled) return;
                        const request = buildRowOutputRequest(contextRowItem);
                        setRowContextMenu(null);
                        dataTableOutputExport.openCorporateOutputForRequest(request);
                    }}
                >
                    <span className="datatable-row-context-menu-icon">
                        <Icon name="file-text" size={18} />
                    </span>
                    <span className="datatable-row-context-menu-text">
                        <span className="datatable-row-context-menu-label">{dataTableLabels.rowCorporateOutputLabel}</span>
                        <span className="datatable-row-context-menu-description">
                            {corporateOutputOperation.disabled
                                ? corporateOutputOperation.description
                                : dataTableLabels.rowCorporateOutputDescription}
                        </span>
                    </span>
                </button>
            ) : null}
            {contextRowActions.map((action) => {
                const allowedByPermission = action.permission ? hasPermission(action.permission) : true;
                const disabled = !allowedByPermission || isRowActionDisabled(action, contextRowItem, contextRowIndex);
                return (
                    <button
                        key={action.key}
                        type="button"
                        className="datatable-row-context-menu-item"
                        role="menuitem"
                        disabled={disabled}
                        title={disabled && action.disabledReason ? action.disabledReason : action.title || action.label}
                        onClick={() => {
                            if (disabled) return;
                            setRowContextMenu(null);
                            void action.onSelect(contextRowItem, contextRowIndex);
                        }}
                    >
                        <span className="datatable-row-context-menu-icon">
                            {action.icon ? <Icon name={action.icon} size={18} /> : null}
                        </span>
                        <span className="datatable-row-context-menu-text">
                            <span className="datatable-row-context-menu-label">{action.label}</span>
                            {disabled && action.disabledReason ? (
                                <span className="datatable-row-context-menu-description">{action.disabledReason}</span>
                            ) : null}
                        </span>
                    </button>
                );
            })}
            {resolvedRowActions?.render ? (
                <div
                    className="datatable-row-context-menu-legacy-actions"
                    role="group"
                    aria-label={resolvedRowActions.ariaLabel}
                >
                    {resolvedRowActions.render(contextRowItem)}
                </div>
            ) : null}
        </div>
    ) : null;

    const clientPaginationNode = clientPaginationEnabled && !loading ? (
        <Pagination
            currentPage={clientPage}
            totalPages={clientTotalPages}
            pageSize={clientPageSize}
            totalItems={controlledData.length}
            onPageChange={setClientPage}
            onPageSizeChange={(nextPageSize) => {
                setClientPageSize(normalizePageSize(nextPageSize));
                setClientPage(1);
            }}
            language={language}
        />
    ) : null;

    const dataTableShellNode = (
        <div
            className={`datatable-shell${clientInlinePaginationEnabled ? ' datatable-inline-paginated' : ''}${fillAvailableHeight ? ' datatable-fill-available' : ''}${clientInlinePaginationEnabled && inlineSurface === 'flat' ? ' datatable-inline-flat' : ''}`}
            ref={shellRef}
            data-workspace-focus-shortcut-disabled={workspaceFocusEnabled ? undefined : 'true'}
            onPointerEnter={workspaceFocusMode.markPreferred}
            onFocusCapture={workspaceFocusMode.markPreferred}
        >
            {effectivePreferencesKey && prefs.loaded && (!isEditInteraction || resolvedToolbarLeadingContent) && (
                <TableToolsSurface ref={toolbarRef} focusControl={workspaceFocusEnabled}>
                    <div className="datatable-toolbar-left">
                        {!isEditInteraction && (
                            <button
                                type="button"
                                className={`datatable-icon-btn datatable-selection-mode-btn${selectionColumnActive ? ' is-active' : ''}`}
                                onClick={handleSelectionModeToggle}
                                aria-pressed={selectionColumnActive}
                                aria-label={dataTableLabels.selectionModeLabel}
                                title={dataTableLabels.selectionModeLabel}
                            >
                                <Icon name={selectionColumnActive ? 'selectRowsChecked' : 'selectRows'} size={19} />
                            </button>
                        )}
                        {resolvedToolbarLeadingContent && (
                            <div className="datatable-toolbar-leading">
                                {resolvedToolbarLeadingContent}
                            </div>
                        )}
                    </div>

                    {!isEditInteraction && <div className="datatable-toolbar-actions">
                        {workspaceFocusEnabled && <WorkspaceFocusModeToggle
                            className={`datatable-icon-btn datatable-focus-mode-btn${focusModeActive ? ' is-active' : ''}${focusModeFullscreen ? ' is-fullscreen' : ''}`}
                            mode={workspaceFocusMode.level}
                            enterLabel={dataTableLabels.focusModeEnterLabel}
                            fullscreenLabel={dataTableLabels.focusModeFullscreenLabel}
                            exitLabel={dataTableLabels.focusModeExitLabel}
                            onToggle={() => {
                                setSettingsMenuOpen(false);
                                setColumnMenuOpen(false);
                                workspaceFocusMode.advance();
                            }}
                            data-datatable-focus-toggle="true"
                        >
                            <Icon name={focusModeFullscreen ? 'focusModeExit' : 'focusModeEnter'} size={18} />
                        </WorkspaceFocusModeToggle>}
                        {!hideRefresh && (
                            <button
                                type="button"
                                className="datatable-icon-btn"
                                onClick={handleRefresh}
                                disabled={loading}
                                title={dataTableLabels.refreshLabel}
                                aria-label={dataTableLabels.refreshLabel}
                            >
                                <Icon name={loading ? 'loading' : 'refresh'} size={18} />
                            </button>
                        )}
                        <div className="datatable-column-menu-wrap">
                            <button
                                ref={settingsButtonRef}
                                type="button"
                                className="datatable-column-btn datatable-settings-btn"
                                onClick={handleSettingsMenuToggle}
                                aria-expanded={settingsMenuOpen}
                                aria-label={dataTableLabels.tableSettingsLabel}
                                title={dataTableLabels.tableSettingsLabel}
                            >
                                <Icon name="settings" size={18} />
                                <span>{dataTableLabels.tableSettingsLabel}</span>
                                <Icon name="chevronDown" size={14} />
                            </button>
                            {settingsMenuOpen && (
                                <div ref={settingsMenuRef} className="datatable-settings-menu" role="menu" style={settingsMenuStyle}>
                                    <div className="datatable-settings-section">
                                        <div className="datatable-settings-section-title">{dataTableLabels.appearanceSectionLabel}</div>
                                        <div className="datatable-row-mode-list" role="group" aria-label={dataTableLabels.rowModeLabel}>
                                            {[
                                                ['ellipsis', dataTableLabels.rowModeEllipsis],
                                                ['scroll', dataTableLabels.rowModeScroll],
                                                ['wrap', dataTableLabels.rowModeWrap],
                                            ].map(([mode, label]) => (
                                                <button
                                                    key={mode}
                                                    type="button"
                                                    className={`datatable-row-mode-option${effectiveRowMode === mode ? ' active' : ''}`}
                                                    onClick={() => prefs.setTableRowMode(mode as DataTableRowMode)}
                                                >
                                                    <Icon name={effectiveRowMode === mode ? 'check' : 'visibility'} size={15} />
                                                    <span>{label}</span>
                                                </button>
                                            ))}
                                        </div>
                                        {prefs.hasStoredTablePrefs && (
                                            <button
                                                type="button"
                                                className="datatable-row-mode-option datatable-reset-option"
                                                onClick={() => prefs.clearTablePrefs()}
                                                title={dataTableLabels.resetThisTablePrefsTitle}
                                            >
                                                <Icon name="undo" size={15} />
                                                <span>{dataTableLabels.resetThisTablePrefs}</span>
                                            </button>
                                        )}
                                    </div>
                                    <div className="datatable-settings-section">
                                        <button
                                            ref={columnSettingsButtonRef}
                                            type="button"
                                            className="datatable-settings-section-toggle"
                                            onClick={() => setColumnMenuOpen((v) => !v)}
                                            aria-expanded={columnMenuOpen}
                                        >
                                            <span>{dataTableLabels.columnsSectionLabel}</span>
                                            <Icon name={columnMenuOpen ? 'chevronUp' : 'chevronDown'} size={15} />
                                        </button>
                                        {columnMenuOpen && (
                                            <div className="datatable-settings-columns" role="group" aria-label={dataTableLabels.columnsLabel}>
                                                {columnSettingsItems.standard
                                                    .map((c) => {
                                                        const id = getColumnPreferenceId(c);
                                                        const visible = !hiddenIds.has(id);
                                                        const label = typeof c.title === 'string' ? c.title : id;
                                                        return (
                                                            <label key={id} className="datatable-column-menu-item">
                                                                <input
                                                                    type="checkbox"
                                                                    className="qubepro-checkbox"
                                                                    checked={visible}
                                                                    onChange={(ev) => toggleColumnVisibility(id, ev.target.checked)}
                                                                />
                                                                <span>{label}</span>
                                                            </label>
                                                        );
                                                    })}
                                                {columnSettingsItems.customFields.length > 0 && (
                                                    <>
                                                        <div className="datatable-settings-columns-divider" role="separator" />
                                                        <div className="datatable-settings-columns-group-title">
                                                            {dataTableLabels.customFieldColumnsSectionLabel}
                                                        </div>
                                                        {columnSettingsItems.customFields.map((c) => {
                                                            const id = getColumnPreferenceId(c);
                                                            const visible = !hiddenIds.has(id);
                                                            const label = typeof c.title === 'string' ? c.title : id;
                                                            return (
                                                                <label key={id} className="datatable-column-menu-item">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="qubepro-checkbox"
                                                                        checked={visible}
                                                                        onChange={(ev) => toggleColumnVisibility(id, ev.target.checked)}
                                                                    />
                                                                    <span>{label}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {resolvedDataOperations.length > 0 && (
                                        <div className="datatable-settings-section">
                                            <div className="datatable-settings-section-title">{dataTableLabels.dataOperationsLabel}</div>
                                            <div className="datatable-settings-operations" role="group" aria-label={dataTableLabels.dataOperationsLabel}>
                                                {corporateOutputOperation && (
                                                    <button
                                                        type="button"
                                                        className="datatable-operations-menu-item datatable-operations-menu-item-primary"
                                                        disabled={corporateOutputOperation.disabled}
                                                        aria-disabled={corporateOutputOperation.disabled}
                                                        title={corporateOutputOperation.description || corporateOutputOperation.label}
                                                        onClick={() => {
                                                            if (corporateOutputOperation.disabled || !corporateOutputOperation.onClick) return;
                                                            // The menu disappears when a modal opens. Keep its stable trigger as the opener.
                                                            settingsButtonRef.current?.focus({ preventScroll: true });
                                                            void corporateOutputOperation.onClick();
                                                            setSettingsMenuOpen(false);
                                                        }}
                                                    >
                                                        <span className="datatable-operations-menu-item-icon">
                                                            <Icon name={corporateOutputOperation.icon} size={18} />
                                                        </span>
                                                        <span className="datatable-operations-menu-item-text">
                                                            <span className="datatable-operations-menu-item-label">{corporateOutputOperation.label}</span>
                                                            {corporateOutputOperation.description && (
                                                                <span className="datatable-operations-menu-item-description">
                                                                    {corporateOutputOperation.description}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                )}
                                                {bulkDataTransferOperations.length > 0 && (
                                                    <div className="datatable-operation-cluster">
                                                        <button
                                                            ref={bulkTransferButtonRef}
                                                            type="button"
                                                            className="datatable-operation-cluster-toggle"
                                                            aria-expanded={bulkDataTransferOpen}
                                                            onClick={() => setBulkDataTransferOpen((current) => !current)}
                                                        >
                                                            <span className="datatable-operations-menu-item-icon">
                                                                <Icon name="upload" size={18} />
                                                            </span>
                                                            <span className="datatable-operations-menu-item-text">
                                                                <span className="datatable-operations-menu-item-label">
                                                                    {dataTableLabels.bulkDataTransferLabel}
                                                                </span>
                                                                <span className="datatable-operations-menu-item-description">
                                                                    {dataTableLabels.bulkDataTransferDescription}
                                                                </span>
                                                            </span>
                                                            <Icon name={bulkDataTransferOpen ? 'chevronUp' : 'chevronDown'} size={15} />
                                                        </button>
                                                        {bulkDataTransferOpen && (
                                                            <div
                                                                className="datatable-operation-cluster-grid"
                                                                role="group"
                                                                aria-label={dataTableLabels.bulkDataTransferLabel}
                                                            >
                                                                {bulkDataTransferOperations.map((operation) => (
                                                                    <button
                                                                        key={operation.key}
                                                                        type="button"
                                                                        className="datatable-operation-cluster-item"
                                                                        disabled={operation.disabled}
                                                                        aria-disabled={operation.disabled}
                                                                        title={operation.description || operation.label}
                                                                        onClick={() => {
                                                                            if (operation.disabled || !operation.onClick) return;
                                                                            settingsButtonRef.current?.focus({ preventScroll: true });
                                                                            void operation.onClick();
                                                                            setBulkDataTransferOpen(false);
                                                                            setSettingsMenuOpen(false);
                                                                        }}
                                                                    >
                                                                        <Icon name={operation.icon} size={16} />
                                                                        <span>{operation.label}</span>
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>}
                </TableToolsSurface>
            )}
            {bulkActionsNode && (
                <div className="datatable-bulk-actions-row" data-no-row-click="true">
                    {bulkActionsNode}
                </div>
            )}
            <div
                ref={tableViewportRef}
                className={joinClassNames(
                    'datatable-table-viewport',
                    stickyHeader && 'datatable-sticky-header-viewport',
                )}
            >
                <div
                    ref={tableWrapperRef}
                    className={joinClassNames(
                        'table-wrapper',
                        `datatable-mode-${effectiveRowMode}`,
                        stickyHeader && 'datatable-sticky-header',
                    )}
                    role="region"
                    aria-label={dataTableLabels.horizontalScrollLabel}
                    tabIndex={0}
                    onPointerDown={(event) => {
                        if (event.pointerType !== 'mouse') {
                            event.currentTarget.focus({ preventScroll: true });
                        }
                    }}
                >
                    <table className={`products-table ${modeClass}`} style={{ minWidth: `max(100%, ${minimumTableWidth}px)` }}>
                    <colgroup>
                        {selectionEnabled && <col style={{ width: '52px' }} />}
                        {normalizedColumns.map((column) => {
                            const colPid = getColumnPreferenceId(column);
                            return (
                                <col
                                    key={`col-width-${colPid}-${String(column.key)}`}
                                    style={resolvedWidth(column)}
                                />
                            );
                        })}
                    </colgroup>
                    <thead>
                        <tr>
                            {selectionEnabled && (
                                <th
                                    className="selection-column selection-header-cell"
                                    style={{ width: '52px', cursor: 'default' }}
                                >
                                    <div className="selection-checkbox-wrap">
                                        <input
                                            ref={headerCheckboxRef}
                                            type="checkbox"
                                            className="qubepro-checkbox row-selection-checkbox"
                                            checked={allVisibleSelected}
                                            aria-label={rowSelectionLabel || dataTableLabels.selectAllRowsLabel}
                                            onChange={(e) => handleToggleAllRowSelection(visibleRowIds, e.target.checked)}
                                        />
                                    </div>
                                </th>
                            )}
                            {normalizedColumns.map((column) => {
                                const colPid = getColumnPreferenceId(column);
                                const canReorder = Boolean(
                                    effectivePreferencesKey && prefs.loaded && column.key !== 'actions',
                                );
                                const isActiveSortColumn = Boolean(
                                    column.sortable &&
                                    (onSort || defaultColumnControls) &&
                                    String(sortColumn ?? internalSortColumn) === String(column.key),
                                );
                                const ariaSort = isActiveSortColumn
                                    ? (sortColumn ? sortDirection : internalSortDirection) === 'asc'
                                        ? 'ascending'
                                        : 'descending'
                                    : undefined;
                                const thClass =
                                    [
                                        column.key === 'actions' ? 'actions-col' : '',
                                        column.align ? `datatable-align-${column.align}` : '',
                                        column === normalizedColumns[0] && column.pinned === 'start' ? 'datatable-pinned-start' : '',
                                        canReorder && dragOverColId === colPid ? 'datatable-th-drag-over' : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ') || undefined;

                                return (
                                    <th
                                        key={`col-${colPid}-${String(column.key)}`}
                                        onClick={() => handleSortClick(column)}
                                        aria-sort={ariaSort}
                                        style={{
                                            ...resolvedWidth(column),
                                            cursor: column.sortable ? 'pointer' : 'default',
                                        }}
                                        className={thClass}
                                        onDragOver={
                                            canReorder
                                                ? (e) => {
                                                      e.preventDefault();
                                                      e.dataTransfer.dropEffect = 'move';
                                                      setDragOverColId(colPid);
                                                  }
                                                : undefined
                                        }
                                        onDragLeave={
                                            canReorder
                                                ? (e) => {
                                                      if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                                          setDragOverColId(null);
                                                      }
                                                  }
                                                : undefined
                                        }
                                        onDrop={canReorder ? (e) => handleColumnDrop(e, colPid) : undefined}
                                    >
                                        <div className="th-content">
                                            <div className="th-title"
                                                role={column.sortable ? 'button' : undefined}
                                                tabIndex={column.sortable ? 0 : undefined}
                                                onKeyDown={event => {
                                                    if (event.target === event.currentTarget && column.sortable && (event.key === 'Enter' || event.key === ' ')) {
                                                        event.preventDefault(); event.stopPropagation(); handleSortClick(column);
                                                    }
                                                }}>
                                                {canReorder && (
                                                    <span
                                                        className="datatable-th-drag-handle"
                                                        draggable
                                                        onDragStart={(e) => handleColumnDragStart(e, colPid)}
                                                        onDragEnd={handleColumnDragEnd}
                                                        onMouseDown={(e) => e.stopPropagation()}
                                                        title={dataTableLabels.reorderColumnHint}
                                                        aria-label={dataTableLabels.reorderColumnHint}
                                                    >
                                                        <Icon name="dragHandle" size={14} />
                                                    </span>
                                                )}
                                                <span>{column.title}</span>
                                                {column.sortable && (onSort || defaultColumnControls) && (
                                                    <span
                                                        className={`sort-icon ${
                                                            String(sortColumn ?? internalSortColumn) === String(column.key) &&
                                                            (isSortManuallyChanged || defaultColumnControls)
                                                                ? 'active'
                                                                : ''
                                                        }`}
                                                    >
                                                        {String(sortColumn ?? internalSortColumn) === String(column.key) &&
                                                        (isSortManuallyChanged || defaultColumnControls) ? (
                                                            (sortColumn ? sortDirection : internalSortDirection) === 'asc' ? (
                                                                <Icon name="arrowUp" size={14} />
                                                            ) : (
                                                                <Icon name="arrowDown" size={14} />
                                                            )
                                                        ) : (
                                                            <Icon name="arrowDown" size={14} />
                                                        )}
                                                    </span>
                                                )}
                                            </div>

                                            {/* Sütun Bazlı Filtre */}
                                            {column.filterable && (
                                                <div className="th-controls" onClick={(e) => e.stopPropagation()}>
                                                    {column.renderFilter ? (
                                                        column.renderFilter()
                                                    ) : column.filterType === 'select' ? (
                                                        <select
                                                            className="column-filter select-filter"
                                                            aria-label={getColumnFilterAccessibleName(column, t.common?.filterByColumn)}
                                                            value={
                                                                column.filterValue ??
                                                                internalColumnFilters[getColumnPreferenceId(column)] ??
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                const next = e.target.value;
                                                                const value = next === 'all' ? '' : next;
                                                                if (column.onFilterChange) {
                                                                    column.onFilterChange(value);
                                                                    return;
                                                                }
                                                                if (defaultColumnControls) {
                                                                    const id = getColumnPreferenceId(column);
                                                                    setInternalColumnFilters((current) => ({ ...current, [id]: value }));
                                                                }
                                                            }}
                                                        >
                                                            <option value="all">{t.common?.all ?? 'All'}</option>
                                                            {column.filterOptions?.map((opt) => (
                                                                <option key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    ) : (
                                                        <input
                                                            type="text"
                                                            className="column-filter"
                                                            aria-label={getColumnFilterAccessibleName(column, t.common?.filterByColumn)}
                                                            placeholder={titleToInputPlaceholder(column.title)}
                                                            value={
                                                                column.filterValue ??
                                                                internalColumnFilters[getColumnPreferenceId(column)] ??
                                                                ''
                                                            }
                                                            onChange={(e) => {
                                                                if (column.onFilterChange) {
                                                                    column.onFilterChange(e.target.value);
                                                                    return;
                                                                }
                                                                if (defaultColumnControls) {
                                                                    const id = getColumnPreferenceId(column);
                                                                    const value = e.target.value;
                                                                    setInternalColumnFilters((current) => ({ ...current, [id]: value }));
                                                                }
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {effectivePreferencesKey && column.key !== 'actions' && (
                                            <div
                                                className="th-resize-handle"
                                                onMouseDown={(e) => startResize(e, colPid)}
                                                data-no-row-click="true"
                                                aria-hidden
                                            />
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan={normalizedColumns.length + (selectionEnabled ? 1 : 0)} className="table-loading">
                                    <Loading />
                                </td>
                            </tr>
                        ) : renderedData.length === 0 ? (
                            <tr>
                                <td colSpan={normalizedColumns.length + (selectionEnabled ? 1 : 0)} className="empty-state">
                                    {emptyMessage || t.common.noData}
                                </td>
                            </tr>
                        ) : (
                            renderedData.map((item, rowIndex) => {
                                const rowClickEnabled = Boolean(onRowClick || (rowContextMenuEnabled && rowActionsTapMode));
                                const rowClassName = joinClassNames(
                                    getRowClassName?.(item),
                                    'datatable-data-row',
                                    rowActionsEnabled && 'datatable-row-has-actions',
                                    rowActionsTapMode && revealedRowActionId === item.id && 'datatable-row-actions-revealed',
                                );
                                return (
                                <Fragment key={item.id}>
                                <tr
                                    key={item.id}
                                    className={rowClassName}
                                    tabIndex={lineActionsKeyboardEnabled ? 0 : rowActionsEnabled ? -1 : undefined}
                                    onFocus={lineActionsKeyboardEnabled ? (event) => {
                                        if (event.target === event.currentTarget && !restoringLineActionFocusRef.current) {
                                            showRowActionsOverlay(item, event.currentTarget);
                                        }
                                    } : undefined}
                                    onBlur={lineActionsKeyboardEnabled ? (event) => {
                                        const target = event.relatedTarget;
                                        if (target instanceof Node && (event.currentTarget.contains(target)
                                            || rowActionsOverlayRef.current?.contains(target))) return;
                                        hideRowActionsOverlay();
                                    } : undefined}
                                    onKeyDown={lineActionsKeyboardEnabled ? (event) => {
                                        if (event.target !== event.currentTarget) return;
                                        if (event.key === 'Escape') {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            hideRowActionsOverlay();
                                        } else if (['Enter', 'F10', 'ArrowRight'].includes(event.key)) {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            showRowActionsOverlay(item, event.currentTarget);
                                            setLineActionFocusRequest(item.id);
                                        }
                                    } : undefined}
                                    onClick={rowClickEnabled ? (event) => handleDataRowClick(event, item) : undefined}
                                    onMouseEnter={rowActionsEnabled ? (event) => showRowActionsOverlay(item, event.currentTarget) : undefined}
                                    onMouseMove={rowActionsEnabled && !rowActionsTapMode ? (event) => {
                                        // Escape/modal teardown may clear the overlay while the
                                        // pointer remains inside this row. Recover on actual
                                        // movement without waiting for a second mouseenter.
                                        if (activeRowActionElementRef.current !== event.currentTarget) {
                                            showRowActionsOverlay(item, event.currentTarget);
                                        }
                                    } : undefined}
                                    onMouseLeave={rowActionsEnabled ? scheduleHideRowActionsOverlay : undefined}
                                    style={rowClickEnabled ? { cursor: 'pointer' } : undefined}
                                    onContextMenu={(event) => {
                                        if (isDataTableBlockedByModal(event.currentTarget)) return;
                                        if (rowContextMenuEnabled) {
                                            event.preventDefault();
                                            openRowContextMenu(item, event.clientX, event.clientY);
                                            return;
                                        }
                                        onRowContextMenu?.(event, item);
                                    }}
                                >
                                    {selectionEnabled && (
                                        <td className="selection-column" onClick={(e) => e.stopPropagation()}>
                                            <div className="selection-checkbox-wrap">
                                                <input
                                                    type="checkbox"
                                                    className="qubepro-checkbox row-selection-checkbox"
                                                    checked={currentSelectedIds.has(item.id)}
                                                    aria-label={`${rowSelectionLabel || dataTableLabels.selectRowLabel} ${rowIndex + 1}`}
                                                    onChange={(e) => handleToggleRowSelection(item.id, e.target.checked)}
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                            </div>
                                        </td>
                                    )}
                                    {normalizedColumns.map((column) => {
                                        const colPid = getColumnPreferenceId(column);
                                        return (
                                            <td
                                            key={`cell-${String(item.id)}-${colPid}-${String(column.key)}`}
                                            className={joinClassNames(
                                                column.key === 'actions'
                                                    ? 'actions-col'
                                                    : column.render
                                                      ? 'datatable-custom-cell'
                                                      : 'datatable-text-cell',
                                                column.align ? `datatable-align-${column.align}` : undefined,
                                                column === normalizedColumns[0] && column.pinned === 'start' ? 'datatable-pinned-start' : undefined,
                                            )}
                                            title={ellipsisTdTitle(effectiveRowMode, column, item)}
                                        >
                                            {column.render ? (
                                                column.render(item)
                                            ) : (
                                                <DefaultTableCell value={item[column.key as keyof T] as unknown} />
                                            )}
                                            </td>
                                        );
                                    })}
                                </tr>
                                {activeRowActionId === item.id && rowActionSpace > 0 && (
                                    <tr data-row-actions-space="true" className="datatable-row-actions-space" aria-hidden="true">
                                        <td colSpan={normalizedColumns.length + Number(selectionEnabled)} style={{ height: rowActionSpace }} />
                                    </tr>
                                )}
                                </Fragment>
                                );
                            })
                        )}
                    </tbody>
                    </table>
                </div>
                {stickyHeader && (
                    <span
                        className="datatable-scrollbar-header-gutter"
                        aria-hidden="true"
                    />
                )}
            </div>
            {rowActionsOverlayPortalTarget && rowActionsOverlayNode
                ? createPortal(rowActionsOverlayNode, rowActionsOverlayPortalTarget)
                : null}
            {rowActionsOverlayPortalTarget && rowContextMenuNode
                ? createPortal(rowContextMenuNode, rowActionsOverlayPortalTarget)
                : null}
            {clientInlinePaginationEnabled && clientPaginationNode}
            <DataTableCorporateOutputDialog {...dataTableOutputExport.configurationDialogProps} />
            <DataTableOutputExportDialog {...dataTableOutputExport.dialogProps} />
            <FormModal
                isOpen={importModalOpen}
                onClose={closeImportModal}
                title={dataTableLabels.importModalTitle}
                size="lg"
                closeOnOverlayClick={!importParsing && !importSubmitting}
                footer={
                    <div className="datatable-import-footer">
                        <button
                            type="button"
                            className="datatable-import-btn datatable-import-btn-secondary"
                            onClick={closeImportModal}
                            disabled={importParsing || importSubmitting}
                        >
                            {dataTableLabels.importCancel}
                        </button>
                        <button
                            type="submit"
                            form={importFormId}
                            className="datatable-import-btn datatable-import-btn-primary"
                            disabled={!importCanSubmit}
                        >
                            {importSubmitting ? dataTableLabels.importSubmitting : dataTableLabels.importSubmit}
                        </button>
                    </div>
                }
            >
                <form
                    id={importFormId}
                    className="datatable-import-panel"
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (importCanSubmit) void handleImportSubmit();
                    }}
                    noValidate
                >
                    <FormBuilder
                        fields={importFormFields}
                        values={{ importFile: null }}
                        onChange={(next) => {
                            void handleImportFileSelect(next.importFile instanceof File ? next.importFile : null);
                        }}
                        language={language}
                        errors={importError && !importPayload ? { importFile: importError } : {}}
                    />

                    <div className="datatable-import-file-name">
                        {importPayload?.fileName || dataTableLabels.importNoFile}
                    </div>

                    {importParsing && (
                        <div className="datatable-import-state">
                            <Icon name="loading" size={16} />
                            <span>{dataTableLabels.importParsing}</span>
                        </div>
                    )}

                    {importMessage && (
                        <div className="datatable-import-alert datatable-import-alert-success">
                            {importMessage}
                        </div>
                    )}

                    {importError && importPayload && (
                        <div className="datatable-import-alert datatable-import-alert-error">
                            {importError}
                        </div>
                    )}

                    {importParseResult && (
                        <>
                            <div className="datatable-import-summary" aria-label={dataTableLabels.importSummary}>
                                <div>
                                    <span>{dataTableLabels.importReadyRows}</span>
                                    <strong>{importParseResult.validRows.length}</strong>
                                </div>
                                <div>
                                    <span>{dataTableLabels.importErrorRows}</span>
                                    <strong>{importParseResult.errors.length}</strong>
                                </div>
                                <div>
                                    <span>{dataTableLabels.importWarnings}</span>
                                    <strong>{importParseResult.warnings.length}</strong>
                                </div>
                            </div>

                            {(importParseResult.missingRequiredHeaders.length > 0 ||
                                importParseResult.unknownHeaders.length > 0 ||
                                importParseResult.errors.length > 0 ||
                                importParseResult.warnings.length > 0) && (
                                <div className="datatable-import-issues">
                                    {importParseResult.missingRequiredHeaders.length > 0 && (
                                        <div>
                                            <strong>{dataTableLabels.importMissingRequiredHeaders}</strong>
                                            <ul>
                                                {importParseResult.missingRequiredHeaders.map((header) => (
                                                    <li key={header}>{header}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {importParseResult.unknownHeaders.length > 0 && (
                                        <div>
                                            <strong>{dataTableLabels.importUnknownHeaders}</strong>
                                            <ul>
                                                {importParseResult.unknownHeaders.map((header) => (
                                                    <li key={header}>{header}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {importParseResult.errors.length > 0 && (
                                        <div>
                                            <strong>{dataTableLabels.importErrorRows}</strong>
                                            <ul>
                                                {importParseResult.errors.slice(0, 8).map((issue, index) => (
                                                    <li key={`${issue.rowNumber ?? 'header'}-${issue.header ?? issue.field ?? index}`}>
                                                        {issue.rowNumber ? `${issue.rowNumber}: ` : ''}
                                                        {issue.message}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {importParseResult.warnings.length > 0 && (
                                        <div>
                                            <strong>{dataTableLabels.importWarnings}</strong>
                                            <ul>
                                                {importParseResult.warnings.slice(0, 8).map((issue, index) => (
                                                    <li key={`${issue.header ?? issue.field ?? index}-${issue.message}`}>
                                                        {issue.rowNumber ? `${issue.rowNumber}: ` : ''}
                                                        {issue.message}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {importPreviewRows.length > 0 && (
                                <div className="datatable-import-preview">
                                    <div className="datatable-import-preview-title">{dataTableLabels.importPreview}</div>
                                    <div className="datatable-import-preview-table-wrap">
                                        <table>
                                            <thead>
                                                <tr>
                                                    <th>#</th>
                                                    {importPreviewHeaders.map((header, index) => (
                                                        <th key={`${header}-${index}`}>{header}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {importPreviewRows.map((row) => (
                                                    <tr key={row.rowNumber} className={row.errors.length ? 'has-errors' : undefined}>
                                                        <td>{row.rowNumber}</td>
                                                        {importPreviewHeaders.map((header, index) => (
                                                            <td key={`${row.rowNumber}-${header}-${index}`}>
                                                                {row.rawValues[index] ?? ''}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </form>
            </FormModal>
        </div>
    );

    return (
        <>
            {dataTableShellNode}
            {clientPagePaginationEnabled && clientPaginationNode}
        </>
    );
}
